/* eslint-disable @typescript-eslint/no-namespace */

/**
 * Represents a command that can be executed by the add-in.
 *
 * @property type - The type of command to execute. Can be either 'openByCaseRefNum' or 'openByAppSerNum'.
 * @property args - The arguments required for the command. The type is generic and can contain any values.
 */
export interface AddinCommand {
  type: 'openByCaseRefNum' | 'openByAppSerNum';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args: any[];
}

/**
 * Namespace containing types and constants related to Outlook categories.
 */
export namespace Outlook {
  /**
   * Represents the possible category types for Outlook items.
   */
  export type CategoryEnum = 'Matter' | 'Patent' | 'Trademark' | 'Reference';

  /**
   * An object containing constants for specific upload status categories in Outlook.
   *
   * - `UPLOADED_CATEGORY`: Indicates the item was uploaded to First To File.
   * - `UPLOADED_CATEGORY_INCOMPLETE`: Indicates the upload to First To File was incomplete.
   * - `UPLOADED_CATEGORY_FAILURE`: Indicates the upload to First To File failed.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const CategoryEnum = Object.freeze({
    UPLOADED_CATEGORY: 'Uploaded to First To File' as CategoryEnum,
    UPLOADED_CATEGORY_INCOMPLETE: 'Incomplete Upload to First To File' as CategoryEnum,
    UPLOADED_CATEGORY_FAILURE: 'Failed to upload to First to File' as CategoryEnum,
  });
}

/**
 * Represents an event message sent to or from a dialog.
 *
 * @property type - The type of the dialog message event.
 * @property data - Optional payload containing additional event data.
 */
export interface DialogMessageEvent {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

/**
 * Represents a group of related files with progress information.
 *
 * @property id - Unique identifier for the file group.
 * @property display - Display name or label for the file group.
 * @property files - Array of files with progress details belonging to this group.
 */
export interface RelatedFileGroup {
  id: string;
  display: string;
  files: FileWithProgress[];
}

/**
 * Transport data that corresponds with a FileWithProgress object
 * to be matched via the id property.
 */
export interface FileData {
  id: string;
  /**
   * base 64 encoded file data
   */
  data: string;
}

/**
 * Represents a file with associated metadata and upload progress information.
 *
 * @property id - Unique identifier for the file.
 * @property name - Name of the file.
 * @property title - Title or description of the file.
 * @property dataFile - The actual file object or a boolean indicating its presence.
 * @property type - MIME type or category of the file.
 * @property size - Size of the file in bytes (optional).
 * @property trifold - Trifold type enumeration value.
 * @property date - Date associated with the file (e.g., upload or creation date).
 * @property subType - Subtype identifier for the file (optional).
 * @property urgent - Indicates if the file is marked as urgent.
 * @property docNotes - Additional notes or comments about the document.
 * @property keywords - Keywords associated with the file for search or categorization.
 * @property hasAttachments - Indicates if the file has attachments.
 * @property isMainEmail - Indicates if the file is the main email (optional).
 * @property entry - Specifies the source of the file (e.g., Outlook API or manual upload).
 * @property referenceType - Reference type identifier (optional).
 * @property exportControl - Indicates if the file is subject to export control regulations.
 */
export interface FileWithProgress {
  id: string;
  name: string;
  title: string;
  dataFile?: File | boolean;
  type: string;
  size?: number;
  trifold: IP.TrifoldEnum;
  date: Date;
  subType?: number;
  urgent: boolean;
  docNotes: string;
  keywords: string;
  hasAttachments: boolean;
  isMainEmail?: boolean;
  // whether the file came from outlook api or manually provided
  entry: UploadEntryType;
  referenceType?: number;
  exportControl: boolean;
}

/**
 * Represents a trifold structure for Common IP, containing an identifier, IP type, and three arrays of files.
 *
 * @property identifier - Unique identifier for the trifold.
 * @property ipType - The type of intellectual property, as defined in IP.TypeEnum.
 * @property L - Array of files associated with the left section.
 * @property M - Array of files associated with the middle section.
 * @property R - Array of files associated with the right section.
 */
export interface CommonIPTrifold {
  identifier: string;
  ipType: IP.TypeEnum;
  L: Array<WsFile>;
  M: Array<WsFile>;
  R: Array<WsFile>;
}

export type UploadEntryType = 'outlook' | 'manual' | 'word';

/**
 * Represents statistics for a common IP operation, including the type of IP,
 * an optional identifier, and lists of files that succeeded or failed.
 *
 * @property item - Contains details about the IP, including its type and an optional identifier.
 * @property successFiles - Array of file names that were processed successfully.
 * @property failureFiles - Array of file names that failed to process.
 */
export interface CommonIPStats {
  item: {
    ipType: IP.TypeEnum;
    identifier?: string;
  };
  successFiles: string[];
  failureFiles: string[];
}

/**
 * Represents statistics for an upload operation.
 *
 * @property items - An array of `CommonIPStats` objects containing statistics for each item.
 * @property emails - An object mapping email addresses (as keys) to their upload results.
 *   Each email entry contains:
 *   - `success`: The number of successful uploads for the email.
 *   - `failure`: The number of failed uploads for the email.
 */
export interface UploadStats {
  items: CommonIPStats[];
  emails: {
    [key: string]: {
      success: number;
      failure: number;
    };
  };
}

/**
 * Represents the response for a common IP trifold upload operation.
 * Extends {@link BaseTrifoldUploadResponse} with additional properties specific to IP uploads.
 *
 * @property ipType - The type of intellectual property, defined by {@link IP.TypeEnum}.
 * @property identifier - A unique identifier for the uploaded IP trifold.
 */
export interface CommonIPTrifoldUploadResponse extends BaseTrifoldUploadResponse {
  ipType: IP.TypeEnum;
  identifier: string;
}

/**
 * Represents a field value that can be displayed, including its key, value, and field name.
 *
 * @property key - The unique identifier for the field value.
 * @property value - The displayable value associated with the field.
 * @property field - The name of the field to which the value belongs.
 */
export interface DisplayableFieldValue {
  key: string;
  value: string;
  field: string;
}

/**
 * Represents the response containing displayable field values and their raw data.
 *
 * @property displayable - An array of displayable field values.
 * @property raw - The raw data associated with the displayable fields.
 */
export interface DisplayableFieldResponse {
  displayable: DisplayableFieldValue[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  raw: any;
}

/**
 * Represents the common intellectual property details.
 *
 * @property references - Optional array of case reference details.
 * @property notes - Array of common notes associated with the IP detail.
 * @property biblio - Bibliographic information (type is any).
 * @property workflow - Array of workflow domain objects.
 */
export interface CommonIPDetail {
  references?: CaseReferenceDetails[];
  notes: CommonNote[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  biblio: any;
  workflow: WorkflowDO[];
}

export type CommonIPStateTabType = 'biblio' | 'trifold' | 'workflow' | 'notes' | 'references';
export type CommonIPStateViewType = 'trifold' | 'detail';

/**
 * Data passed to the CommonIPStateTrifoldFiltersDialog.
 *
 * @property docSubtypeId - Optional array of document subtype IDs to filter by.
 */
export interface CommonIPStateTrifoldFiltersDialogData {
  docSubtypeId?: number[];
}

/**
 * Represents the state of a common IP (Intellectual Property) component.
 *
 * @property expanded - Indicates whether the component is expanded.
 * @property view - Optional view type (trifold | detail) for the component.
 * @property detailTabIndex - Index of the currently selected detail tab.
 * @property trifoldTabIndex - Index of the currently selected trifold tab.
 * @property isDragOver - Indicates whether a drag-over event is occurring.
 * @property trifoldFilters - Optional filters data for the trifold dialog.
 */
export interface CommonIPState {
  expanded: boolean;
  view?: CommonIPStateViewType;
  detailTabIndex: number;
  trifoldTabIndex: number;
  isDragOver: boolean;
  trifoldFilters?: CommonIPStateTrifoldFiltersDialogData;
}

/**
 * The default state for a CommonIPState instance.
 *
 * @property expanded - Indicates whether the item is expanded.
 * @property view - The current view, or undefined if not set.
 * @property detailTabIndex - The index of the selected detail tab, defaults to -1.
 * @property trifoldTabIndex - The index of the selected trifold tab, defaults to -1.
 * @property isDragOver - Indicates whether a drag-over event is active.
 * @property trifoldFilters - Filters applied to trifold, with docSubtypeId as an array.
 */
export const DEFAULT_IP_STATE: CommonIPState = {
  expanded: true,
  view: undefined,
  detailTabIndex: -1,
  trifoldTabIndex: -1,
  isDragOver: false,
  trifoldFilters: { docSubtypeId: [] },
};

/**
 * Represents a lightweight version of a Common IP entity.
 *
 * @property ipType - The type of IP, defined by the `IP.TrifTypeEnum` enumeration.
 * @property identifier - The unique identifier for the IP.
 */
export interface CommonIPLite {
  ipType: IP.TrifTypeEnum;
  identifier: string;
}

/**
 * Represents a comprehensive Intellectual Property (IP) entity, extending the basic properties from `CommonIPLite`.
 *
 * @property {string} family - The IP family or group this entity belongs to.
 * @property {string} [serial] - Optional serial number associated with the IP.
 * @property {string} title - The title or name of the IP.
 * @property {string} [caseUrl] - Optional URL linking to the case details.
 * @property {string} [exportControlStatus] - Optional export control status of the IP.
 * @property {CommonIPTrifold} [trifold] - Optional trifold information related to the IP.
 * @property {CommonIPState} [state] - UI-specific state for tracking view status (not case state).
 * @property {boolean} [favorite] - Indicates if the IP is marked as a favorite in the UI.
 * @property {CommonIPDetail} [detail] - Additional details or metadata about the IP.
 */
export interface CommonIP extends CommonIPLite {
  family: string;
  serial?: string;
  title: string;
  caseUrl?: string;
  exportControlStatus?: string;
  trifold?: CommonIPTrifold;

  // track state for ui (this is view state not case state)
  state?: CommonIPState;
  favorite?: boolean;
  // other keys
  detail?: CommonIPDetail;
}

export interface ExcelActionDetails {
  action: EA.ActionEnum;
  actionType: EA.ActionTypeEnum;

  sourceType: EA.SourceTypeEnum;
  source?: null | string | CommonIPLite[];
}

/**
 * Namespace containing Excel Action types and constants.
 */
export namespace EA {
  export type SourceTypeEnum = 'Worksheet' | 'Range' | 'Selection';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const SourceTypeEnum = Object.freeze({
    Worksheet: 'Worksheet' as SourceTypeEnum,
    Range: 'Range' as SourceTypeEnum,
    Selection: 'Selection' as SourceTypeEnum,
  });

  export type ActionTypeEnum = IP.TypeEnum | 'TrademarkImage'; // etc...
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const ActionTypeEnum = Object.freeze({
    Matter: 'Matter' as ActionTypeEnum,
    Patent: 'Patent' as ActionTypeEnum,
    Trademark: 'Trademark' as ActionTypeEnum,
    Reference: 'Reference' as ActionTypeEnum,
    CaseReference: 'CaseReference' as ActionTypeEnum,
    TrademarkImage: 'TrademarkImage' as ActionTypeEnum,
  });

  export type ActionEnum = 'upload' | 'download';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const ActionEnum = Object.freeze({
    download: 'download' as ActionEnum,
    upload: 'upload' as ActionEnum,
  });
}

/**
 * Namespace containing workflow-related types and constants.
 */
export namespace WF {
  /**
   * Represents the possible actions that can be performed on the current task.
   * - `'ClaimCurrentTask'`: Claim the current task.
   * - `'UnclaimCurrentTask'`: Unclaim the current task.
   */
  export type ActionEnum = 'ClaimCurrentTask' | 'UnclaimCurrentTask';

  /**
   * An immutable object containing the possible values for {@link ActionEnum}.
   * - `ClaimCurrentTask`: Represents claiming the current task.
   * - `UnclaimCurrentTask`: Represents unclaiming the current task.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const ActionEnum = Object.freeze({
    ClaimCurrentTask: 'ClaimCurrentTask' as ActionEnum,
    UnclaimCurrentTask: 'UnclaimCurrentTask' as ActionEnum,
  });
}

/**
 * Namespace containing types and constants related to IP entities for the FTF API model.
 */
export namespace IP {
  /**
   * An enumeration representing the different types of IP entities.
   *
   * @enum {string}
   * @property {string} Matter - Represents a Matter type.
   * @property {string} Patent - Represents a Patent type.
   * @property {string} Trademark - Represents a Trademark type.
   * @property {string} Reference - Represents a Reference type.
   * @property {string} CaseReference - Represents a Case Reference type.
   */
  export type TypeEnum = 'Matter' | 'Patent' | 'Trademark' | 'Reference' | 'CaseReference';

  /**
   * Enum-like object representing various types used in the FTF API model.
   *
   * @remarks
   * This object is frozen to prevent modification at runtime.
   *
   * @property {TypeEnum} Matter - Represents a matter type.
   * @property {TypeEnum} Patent - Represents a patent type.
   * @property {TypeEnum} Trademark - Represents a trademark type.
   * @property {TypeEnum} Reference - Represents a reference type.
   * @property {TypeEnum} CaseReference - Represents a case reference type.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const TypeEnum = Object.freeze({
    Matter: 'Matter' as TypeEnum,
    Patent: 'Patent' as TypeEnum,
    Trademark: 'Trademark' as TypeEnum,
    Reference: 'Reference' as TypeEnum,
    CaseReference: 'CaseReference' as TypeEnum,
  });

  // Define a type that excludes 'Reference'
  export type TrifTypeEnum = Exclude<keyof typeof IP.TypeEnum, 'Reference' | 'CaseReference'>;

  /**
   * An enumeration representing the different types of TRIF entities.
   *
   * @enum {string}
   * @property {string} Matter - Represents a Matter type.
   * @property {string} Patent - Represents a Patent type.
   * @property {string} Trademark - Represents a Trademark type.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const TrifTypeEnum = Object.freeze({
    Matter: 'Matter' as TrifTypeEnum,
    Patent: 'Patent' as TrifTypeEnum,
    Trademark: 'Trademark' as TrifTypeEnum,
  });

  export type TrifoldEnum = 'L' | 'M' | 'R';

  /**
   * An immutable enumeration representing the possible trifold values.
   *
   * - `L`: Left
   * - `M`: Middle
   * - `R`: Right
   *
   * @readonly
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const TrifoldEnum = Object.freeze({
    L: 'L' as TrifoldEnum,
    M: 'M' as TrifoldEnum,
    R: 'R' as TrifoldEnum,
  });

  /**
   * An immutable object mapping numeric reference type identifiers to their corresponding string descriptions.
   *
   * @remarks
   * - `1`: Non-Patent Reference
   * - `2`: Foreign Patent
   * - `3`: US Patent
   *
   * @readonly
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const ReferenceTypes = Object.freeze({
    1: 'Non-Patent Reference',
    2: 'Foreign Patent',
    3: 'US Patent',
  });
}

export namespace Sorting {
  export type DirectionEnum = 'asc' | 'desc';

  /**
   * An enumeration representing sorting directions.
   *
   * @remarks
   * This object is frozen to prevent modification.
   *
   * @property ASC - Represents ascending order with value 'asc' and label 'Ascending'.
   * @property DESC - Represents descending order with value 'desc' and label 'Descending'.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const DirectionEnum = Object.freeze({
    ASC: {
      value: 'asc' as DirectionEnum,
      label: 'Ascending',
    },
    DESC: {
      value: 'desc' as DirectionEnum,
      label: 'Descending',
    },
  });

  export type SortByEnum = 'title' | 'document_date' | 'document_sub_type' | 'file_name';

  /**
   * Enum-like object representing the available sorting options for documents.
   * Each property contains a `value` used for sorting and a human-readable `label`.
   *
   * @remarks
   * This object is frozen to prevent modification at runtime.
   *
   * @example
   * ```
   * SortByEnum.TITLE.value // 'title'
   * SortByEnum.TITLE.label // 'Title'
   * ```
   *
   * @property TITLE - Sort by document title.
   * @property DOCUMENT_DATE - Sort by document date.
   * @property DOCUMENT_SUB_TYPE - Sort by document sub-type.
   * @property FILE_NAME - Sort by file name.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const SortByEnum = Object.freeze({
    TITLE: {
      value: 'title' as SortByEnum,
      label: 'Title',
    },
    DOCUMENT_DATE: {
      value: 'document_date' as SortByEnum,
      label: 'Document Date',
    },
    DOCUMENT_SUB_TYPE: {
      value: 'document_sub_type' as SortByEnum,
      label: 'Document Sub-Type',
    },
    FILE_NAME: {
      value: 'file_name' as SortByEnum,
      label: 'File Name',
    },
  });
}

/**
 * An immutable object mapping numeric action type identifiers to their corresponding document action names.
 *
 * @remarks
 * The keys represent action type IDs, and the values are their string descriptions.
 *
 * @example
 * DOC_ACTION_TYPES[1] // "Details"
 * DOC_ACTION_TYPES[2] // "Preview"
 * DOC_ACTION_TYPES[3] // "Download"
 */
export const DOC_ACTION_TYPES = Object.freeze({
  1: 'Details',
  2: 'Preview',
  3: 'Download',
});

/**
 * An immutable mapping of IP type identifiers to their corresponding upload bar labels.
 *
 * @remarks
 * Currently, only the identifier `1` is mapped to `'Reference Library'`.
 *
 */
export const DEFAULT_IP_TYPES_UPLOAD_BAR = Object.freeze({
  1: 'Reference Library',
});

export type UploadSubTypeGroupingMode = 'Doc Type' | 'Sub-Type';

/**
 * An immutable object representing the grouping modes for upload sub-types.
 *
 * @property {UploadSubTypeGroupingMode} DocType - Groups by document type.
 * @property {UploadSubTypeGroupingMode} DocSubType - Groups by document sub-type.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const UploadSubTypeGroupingMode = Object.freeze({
  DocType: 'Doc Type' as UploadSubTypeGroupingMode,
  DocSubType: 'Doc Sub-Type' as UploadSubTypeGroupingMode,
});

/**
 * Maps each `IP.TrifTypeEnum` value to its corresponding property key in the `DocumentSubType` interface.
 *
 * - `Matter` maps to `'usedInOm'`
 * - `Patent` maps to `'usedInPatent'`
 * - `Trademark` maps to `'usedInTm'`
 *
 * The mapping is immutable and used to associate intellectual property types with their relevant document subtype properties.
 */
export const DST_PROP_BY_IP_TYPE: { [key in IP.TrifTypeEnum]: keyof DocumentSubType } = Object.freeze({
  Matter: 'usedInOm',
  Patent: 'usedInPatent',
  Trademark: 'usedInTm',
});

/**
 * Maps each `IP.TrifTypeEnum` value to its corresponding trifold property key in the `DocumentSubType` interface.
 *
 * - `Matter` maps to `'omTrifold'`
 * - `Patent` maps to `'patentTrifold'`
 * - `Trademark` maps to `'tmTrifold'`
 *
 * This mapping is immutable and is used to associate intellectual property types with their relevant trifold properties in document subtypes.
 */
export const DST_TRIFOLD_PROP_BY_IP_TYPE: { [key in IP.TrifTypeEnum]: keyof DocumentSubType } = Object.freeze({
  Matter: 'omTrifold',
  Patent: 'patentTrifold',
  Trademark: 'tmTrifold',
});

/**
 * Represents an object where each key maps to an array of `DocumentSubType` objects.
 *
 * @remarks
 * The keys are strings, and the values are arrays containing instances of `DocumentSubType`.
 *
 */
export interface NestedSubType {
  [key: string]: DocumentSubType[];
}

// subtypes bucketized by IP Type
export type SubTypeByIpType = { [key in IP.TypeEnum]: NestedSubType };
export type ValueOf<T> = T[keyof T];
export type Entries<T> = [keyof T, ValueOf<T>][];

/**
 * Represents the permissions available for Trifold operations.
 *
 * @property access - List of permissions related to accessing files.
 * @property upload - List of permissions related to uploading files.
 * @property version - List of permissions related to versioning files.
 * @property lock - List of permissions related to locking files.
 * @property deleteFile - List of permissions related to deleting files.
 */
export interface TrifoldPermissions {
  access: string[];
  upload: string[];
  version: string[];
  lock: string[];
  deleteFile: string[];
}

/**
 * Represents a mapping between a field and its default value, endpoint, display preference, and required permissions.
 *
 * @property default - The default value for the field.
 * @property field - The name of the field.
 * @property endpoint - The API endpoint associated with the field.
 * @property displayPreference - The key in preferences indicating whether the field should be displayed.
 * @property permissions - The permission keys required to access and upload to this trifold.
 */
export interface FieldMapWithDefault {
  default: string;
  field: string;
  endpoint: string;
  // key in preferences whether it should be displayed
  displayPreference: string;
  // permission keys required to access and upload to this trifold
  permissions: TrifoldPermissions;
}

/**
 * Represents a mapping from each value of `IP.TrifoldEnum` to its corresponding `FieldMapWithDefault`.
 * Useful for associating trifold enum values with their default field mappings.
 */
export type TrifoldFieldMap = {
  [key in IP.TrifoldEnum]: FieldMapWithDefault;
};

/**
 * Represents a mapping of `TrifoldFieldMap` objects keyed by each value of `IP.TrifTypeEnum`.
 * Each key corresponds to a specific `TrifTypeEnum` value, and its value is the associated `TrifoldFieldMap`.
 */
export type TrifoldFieldMapByIPType = {
  [key in IP.TrifTypeEnum]: TrifoldFieldMap;
};

/**

 * Immutable mapping object that defines the configuration for trifold fields by IP type (Patent, Trademark, Matter).
 * Each IP type contains three sections (L, M, R) representing different document categories.
 * For each section, the mapping includes:
 * - `default`: The default display name for the section.
 * - `field`: The corresponding field name in the data model.
 * - `endpoint`: The API endpoint associated with the section.
 * - `displayPreference`: The display preference identifier.
 * - `permissions`: An object specifying required roles for access, upload, versioning, locking, and deleting files.
 *
 * This mapping is used to control UI display and permission checks for trifold document management.
 *
 * @constant
 * @name TRIFOLD_FIELD_MAP
 * @description
 *
 */
export const TRIFOLD_FIELD_MAP: TrifoldFieldMapByIPType = Object.freeze({
  Patent: {
    L: {
      default: 'Correspondence',
      field: 'casetrifold.correspondence',
      endpoint: 'correspondence',
      displayPreference: 'FoldCor',
      permissions: {
        access: ['ROLE_CORRESPONDENCE_ACCESS'],
        upload: ['ROLE_CORRESPONDENCE_UPLOAD'],
        version: ['ROLE_CASE_MODIFY', 'ROLE_CORRESPONDENCE_VERSION'],
        lock: ['ROLE_CASE_MODIFY', 'ROLE_CORRESPONDENCE_LOCK'],
        deleteFile: ['ROLE_CORRESPONDENCE_DELETE'],
      },
    },
    M: {
      default: 'File History',
      field: 'casetrifold.filehistory',
      endpoint: 'filing',
      displayPreference: 'FoldFil',
      permissions: {
        access: ['ROLE_PATENT_FILE_HISTORY_ACCESS'],
        upload: ['ROLE_FILE_HISTORY_UPLOAD'],
        version: ['ROLE_CASE_MODIFY', 'ROLE_FILE_HISTORY_VERSION'],
        lock: ['ROLE_CASE_MODIFY', 'ROLE_FILE_HISTORY_LOCK'],
        deleteFile: ['ROLE_FILE_HISTORY_DELETE'],
      },
    },
    R: {
      default: 'Documents',
      field: 'casetrifold.documents',
      endpoint: 'documents',
      displayPreference: 'FoldDoc',
      permissions: {
        access: ['ROLE_PATENT_DOCUMENTS_ACCESS'],
        upload: ['ROLE_DOCUMENTS_UPLOAD'],
        version: ['ROLE_CASE_MODIFY', 'ROLE_DOCUMENTS_VERSION'],
        lock: ['ROLE_CASE_MODIFY', 'ROLE_DOCUMENTS_LOCK'],
        deleteFile: ['ROLE_DOCUMENTS_DELETE'],
      },
    },
  },
  Trademark: {
    L: {
      default: 'Client Corr.',
      field: 'trademarktrifold.clientcorr',
      endpoint: 'client',
      displayPreference: 'FoldCli',
      permissions: {
        access: ['ROLE_TRADEMARK_CLIENT_CORR_ACCESS'],
        upload: ['ROLE_TRADEMARK_CLIENT_UPLOAD'],
        version: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_CLIENT_VERSION'],
        lock: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_CLIENT_LOCK'],
        deleteFile: ['ROLE_TRADEMARK_CLIENT_DELETE'],
      },
    },
    M: {
      default: 'TM Office Corr.',
      field: 'trademarktrifold.tmofficecorr',
      endpoint: 'tm_office',
      displayPreference: 'FoldTmOf',
      permissions: {
        access: ['ROLE_TRADEMARK_OFFICE_CORR_ACCESS'],
        upload: ['ROLE_TRADEMARK_TM_OFFICE_UPLOAD'],
        version: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OFFICE_VERSION'],
        lock: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OFFICE_LOCK'],
        deleteFile: ['ROLE_TRADEMARK_TM_OFFICE_DELETE'],
      },
    },
    R: {
      default: 'Opp Counsel Corr.',
      field: 'trademarktrifold.oppcounselcorr',
      endpoint: 'opposing_counsel',
      displayPreference: 'FoldCol',
      permissions: {
        access: ['ROLE_TRADEMARK_OPP_COUNSEL_CORR_ACCESS'],
        upload: ['ROLE_TRADEMARK_TM_OPPOSING_COUNSEL_UPLOAD'],
        version: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OPPOSING_COUNSEL_VERSION'],
        lock: ['ROLE_TRADEMARK_MODIFY', 'ROLE_TRADEMARK_TM_OPPOSING_COUNSEL_LOCK'],
        deleteFile: ['ROLE_TRADEMARK_TM_OPPOSING_COUNSEL_DELETE'],
      },
    },
  },
  Matter: {
    L: {
      default: 'Correspondence',
      field: 'mattertrifold.correspondence',
      endpoint: 'correspondence',
      displayPreference: 'FoldMCor',
      permissions: {
        access: ['ROLE_MATTER_CORRESPONDENCE_ACCESS'],
        upload: ['ROLE_MATTER_CORR_UPLOAD'],
        version: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_CORR_VERSION'],
        lock: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_CORR_LOCK'],
        deleteFile: ['ROLE_MATTER_CORR_DELETE'],
      },
    },
    M: {
      default: 'File History',
      field: 'mattertrifold.filehistory',
      endpoint: 'filing',
      displayPreference: 'FoldMFil',
      permissions: {
        access: ['ROLE_MATTER_FILE_HISTORY_ACCESS'],
        upload: ['ROLE_MATTER_FILE_HIS_UPLOAD'],
        version: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_FILE_HIS_VERSION'],
        lock: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_FILE_HIS_LOCK'],
        deleteFile: ['ROLE_MATTER_FILE_HIS_DELETE'],
      },
    },
    R: {
      default: 'Documents',
      field: 'mattertrifold.documents',
      endpoint: 'documents',
      displayPreference: 'FoldMDoc',
      permissions: {
        access: ['ROLE_MATTER_DOCUMENT_ACCESS'],
        upload: ['ROLE_MATTER_DOCUMENTS_UPLOAD'],
        version: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_DOCUMENTS_VERSION'],
        lock: ['ROLE_MATTER_MODIFY', 'ROLE_MATTER_DOCUMENTS_LOCK'],
        deleteFile: ['ROLE_MATTER_DOCUMENTS_DELETE'],
      },
    },
  },
});

/**
 * An immutable mapping that associates API endpoint paths with their corresponding `IP.TrifoldEnum` values.
 * e.g., 'correspondence' maps to 'L', 'filing' maps to 'M', and 'documents' maps to 'R'.
 */
export const TRIFOLD_ENDPOINT_TO_LETTER_MAP = Object.freeze(
  Object.values(TRIFOLD_FIELD_MAP).reduce(
    (map, fieldMap) =>
      (Object.entries(fieldMap) as Entries<TrifoldFieldMap>).reduce(
        (inner, [letter, value]) => ({ ...inner, [value.endpoint]: letter }),
        map
      ),
    {} as { [key: string]: IP.TrifoldEnum }
  )
);

/**
 * Represents a key-value pair where the key is of generic type `T` and the value is a string label.
 *
 * @template T - The type of the key.
 * @property {T} key - The key associated with the label.
 * @property {string} value - The string label corresponding to the key.
 */
export interface IpTypeLabel<T> {
  key: T;
  value: string;
}

export const IP_TYPE_DEFAULTS: Array<IpTypeLabel<IP.TrifTypeEnum>> = [
  { key: IP.TrifTypeEnum.Patent, value: 'Patent' },
  { key: IP.TrifTypeEnum.Trademark, value: 'Trademark' },
  { key: IP.TrifTypeEnum.Matter, value: 'Other Matter' },
];

export type TrifoldOrderMap = { [key in IP.TrifTypeEnum]: IP.TrifoldEnum[] };

/**
 * Immutable default mapping for trifold order configurations.
 *
 * Each key represents a category (e.g., 'Patent', 'Trademark', 'Matter') and maps to an array
 * specifying the default order of trifold sections: 'L' (Left), 'M' (Middle), and 'R' (Right).
 *
 */
export const TRIFOLD_ORDER_DEFAULTS: TrifoldOrderMap = Object.freeze({
  Patent: ['L', 'M', 'R'],
  Trademark: ['L', 'M', 'R'],
  Matter: ['L', 'M', 'R'],
});

/**
 * Represents a mapping of trifold details, where each key is a `TrifTypeEnum` and maps to another object.
 * The inner object maps each `TrifoldEnum` to an object containing properties for label, access, upload, version, lock, and deleteFile.
 *
 * @remarks
 * This type is useful for managing permissions and metadata for different trifold types and their corresponding enums.
 *
 */
export type TrifoldDetailMap = {
  [key in IP.TrifTypeEnum]: {
    [key in IP.TrifoldEnum]: { label: string; access: boolean; upload: boolean; version: boolean; lock: boolean; deleteFile: boolean };
  };
};

/**
 * Immutable default configuration for the TrifoldDetailMap, specifying
 * the labels and access permissions for each section (L, M, R) of
 * Patent, Trademark, and Matter entities.
 *
 * Each section includes:
 * - `label`: Display name for the section.
 * - `access`: Whether the section is accessible.
 * - `upload`: Whether uploading is allowed.
 * - `version`: Whether versioning is enabled.
 * - `lock`: Whether locking is enabled.
 * - `deleteFile`: Whether file deletion is permitted.
 *
 * @see TrifoldDetailMap
 */
export const TRIFOLD_MAP_DEFAULTS: TrifoldDetailMap = Object.freeze({
  Patent: {
    L: { label: 'Correspondence', access: true, upload: false, version: false, lock: false, deleteFile: false },
    M: { label: 'File History', access: true, upload: false, version: false, lock: false, deleteFile: false },
    R: { label: 'Documents', access: true, upload: false, version: false, lock: false, deleteFile: false },
  },
  Trademark: {
    L: { label: 'Client Corr.', access: true, upload: false, version: false, lock: false, deleteFile: false },
    M: { label: 'TM Office Corr.', access: true, upload: false, version: false, lock: false, deleteFile: false },
    R: { label: 'Opp Counsel Corr.', access: true, upload: false, version: false, lock: false, deleteFile: false },
  },
  Matter: {
    L: { label: 'Correspondence', access: true, upload: false, version: false, lock: false, deleteFile: false },
    M: { label: 'File History', access: true, upload: false, version: false, lock: false, deleteFile: false },
    R: { label: 'Documents', access: true, upload: false, version: false, lock: false, deleteFile: false },
  },
});

/**
 * A frozen mapping of `TrifTypeEnum` values to their default field names.
 *
 * - `Patent`: Defaults to `'Correspondence'`
 * - `Trademark`: Defaults to `'Client Correspondence'`
 * - `Matter`: Defaults to `'Correspondence'`
 *
 * This constant ensures that each trif type is associated with its respective default field name,
 * and the mapping cannot be modified at runtime.
 */
export const TRIFOLD_FIELD_DEFAULTS: { [key in IP.TrifTypeEnum]: string } = Object.freeze({
  Patent: 'Correspondence',
  Trademark: 'Client Correspondence',
  Matter: 'Correspondence',
});

/**
 * Maps each IP type to its corresponding API endpoint paths for primary entities and associated files.
 *
 * The keys are values from the `IP.TypeEnum` enumeration, and the values are objects containing:
 * - `primary`: The API endpoint for the main entity of the given IP type.
 * - `files`: The API endpoint for files related to the given IP type.
 *
 * @example
 * IP_TYPE_ENDPOINT_MAP[IP.TypeEnum.Patent].primary // returns 'cases'
 * IP_TYPE_ENDPOINT_MAP[IP.TypeEnum.Trademark].files // returns 'trademarkfiles'
 */
export const IP_TYPE_ENDPOINT_MAP: { [key in IP.TypeEnum]: { primary: string; files: string } } = {
  Matter: {
    primary: 'matters',
    files: 'matterfiles',
  },
  Patent: {
    primary: 'cases',
    files: 'casefiles',
  },
  Trademark: {
    primary: 'trademarks',
    files: 'trademarkfiles',
  },
  Reference: {
    primary: 'reference',
    files: 'reference',
  },
  CaseReference: {
    primary: 'reference',
    files: 'reference',
  },
};

/**
 * Represents the general settings for an account.
 *
 * @property {number} [createdByUserId] - The ID of the user who created the setting.
 * @property {string} [displayName] - The display name associated with the setting.
 * @property {string} [genericOne] - A generic field for custom data.
 * @property {string} [genericThree] - A generic field for custom data.
 * @property {string} [genericTwo] - A generic field for custom data.
 * @property {string} [isActive] - Indicates if the setting is active.
 * @property {string} [isCustomFieldName] - Indicates if the field name is custom.
 * @property {string} [searchType] - The type of search associated with the setting.
 * @property {number} [settingId] - The unique identifier for the setting.
 * @property {string} [type] - The type of the setting.
 */
export interface AccountGeneralSettings {
  createdByUserId?: number;
  displayName?: string;
  genericOne?: string;
  genericThree?: string;
  genericTwo?: string;
  isActive?: string;
  isCustomFieldName?: string;
  searchType?: string;
  settingId?: number;
  type?: string;
}

/**
 * Represents an event for adding a file, containing optional details such as case number,
 * display name, event ID, event URL, and file ID.
 *
 * @property caseNumber - (Optional) The case number associated with the event.
 * @property displayName - (Optional) The display name for the event.
 * @property eventId - (Optional) The unique identifier for the event.
 * @property eventUrl - (Optional) The URL related to the event.
 * @property fileId - (Optional) The identifier of the file being added.
 */
export interface AddFileEvent {
  caseNumber?: string;
  displayName?: string;
  eventId?: string;
  eventUrl?: string;
  fileId?: number;
}

/**
 * Represents the details of an agent's case.
 *
 * @property caseFamilyNm - The name of the case family.
 * @property caseId - The unique identifier for the case.
 * @property country - The country associated with the case.
 * @property fileDate - The date the case was filed.
 * @property primaryCaseNm - The primary name of the case.
 * @property serialNumber - The serial number of the case.
 * @property title - The title of the case.
 */
export interface AgentCase {
  caseFamilyNm?: string;
  caseId?: number;
  country?: string;
  fileDate?: Date;
  primaryCaseNm?: string;
  serialNumber?: string;
  title?: string;
}

/**
 * Represents an application type with optional identifier and name.
 *
 * @property id - The unique identifier of the application type.
 * @property name - The display name of the application type.
 */
export interface ApplicationType {
  id?: string;
  name?: string;
}

/**
 * Represents the details of a case in the FTF API.
 *
 * @property applicationType - The type of application associated with the case.
 * @property assignees - The assignees of the case.
 * @property attorneys - The attorneys involved in the case.
 * @property caseFamilyNm - The family name of the case.
 * @property caseId - The unique identifier for the case.
 * @property caseStatus - The current status of the case.
 * @property caseSuffix - The suffix of the case.
 * @property caseType - The type of the case.
 * @property caseUrl - The URL for the case details.
 * @property country - The country associated with the case.
 * @property createByUser - The user who created the case.
 * @property createDate - The date the case was created.
 * @property exportControlStatus - The export control status of the case.
 * @property externalRef - An external reference for the case.
 * @property fileDate - The date the case was filed.
 * @property genericField1 - A generic field for custom data.
 * @property genericField2 - A generic field for custom data.
 * @property genericField3 - A generic field for custom data.
 * @property genericField4 - A generic field for custom data.
 * @property genericField5 - A generic field for custom data.
 * @property genericField6 - A generic field for custom data.
 * @property groups - The groups associated with the case.
 * @property inventors - The inventors listed in the case.
 * @property issueDate - The date the case was issued.
 * @property jurisdiction - The jurisdiction of the case.
 * @property modifiedDate - The date the case was last modified.
 * @property modifyByUser - The user who last modified the case.
 * @property patentNumber - The patent number associated with the case.
 * @property primaryCaseNm - The primary name of the case.
 * @property priorityDate - The priority date of the case.
 * @property priorityNumber - The priority number of the case.
 * @property products - The products related to the case.
 * @property publicationDates - The publication dates of the case.
 * @property publicationNumber - The publication number of the case.
 * @property serialNumber - The serial number of the case.
 * @property technologies - The technologies related to the case.
 * @property title - The title of the case.
 */
export interface CaseDetails {
  applicationType?: string;
  assignees?: string;
  attorneys?: string;
  caseFamilyNm?: string;
  caseId?: number;
  caseStatus?: string;
  caseSuffix?: string;
  caseType?: string;
  caseUrl?: string;
  country?: string;
  createByUser?: string;
  createDate?: Date;
  exportControlStatus?: string;
  externalRef?: string;
  fileDate?: string;
  genericField1?: string;
  genericField2?: string;
  genericField3?: string;
  genericField4?: string;
  genericField5?: string;
  genericField6?: string;
  groups?: string;
  inventors?: string;
  issueDate?: Date;
  jurisdiction?: string;
  modifiedDate?: Date;
  modifyByUser?: string;
  patentNumber?: string;
  primaryCaseNm?: string;
  priorityDate?: Date;
  priorityNumber?: string;
  products?: string;
  publicationDates?: Array<Date>;
  publicationNumber?: string;
  serialNumber?: string;
  technologies?: string;
  title?: string;
}

/**
 * Represents a group of cases with an optional case number and group ID.
 *
 * @property caseNumber - The unique identifier for the case (optional).
 * @property groupId - The identifier for the group (optional).
 */
export interface CaseGroup {
  caseNumber?: string;
  groupId?: number;
}

/**
 * Extends `ReferenceDetails` to provide additional information specific to case references.
 *
 * @property source - The source identifier for the case reference.
 * @property sourceDate - The date associated with the source.
 * @property sourceDisplay - A display string for the source.
 * @property isSubmitted - Indicates if the case reference has been submitted.
 * @property citationNotes - Additional notes related to the citation.
 * @property citedDate - The date when the case was cited.
 * @property citationCategory - The category of the citation.
 */
export interface CaseReferenceDetails extends ReferenceDetails {
  source?: number;
  sourceDate?: Date;
  sourceDisplay?: string;
  isSubmitted?: string;
  citationNotes?: string;
  citedDate?: Date;
  citationCategory?: string;
}

export interface CaseReferenceWithId {
  caseIdentifier?: string;
  references: CaseReferenceDetails[];
}

/**
 * Represents the status of a case.
 *
 * @property id - The unique identifier of the case status.
 * @property name - The display name of the case status.
 */
export interface CaseStatus {
  id?: string;
  name?: string;
}

/**
 * Base interface for family associations with common fields.
 *
 * @property country - The country associated with the case.
 * @property exportControlStatus - The export control status.
 * @property fileDate - The date the case was filed.
 * @property serialNumber - The serial number.
 */
export interface FamilyAssociationBase {
  country?: string;
  exportControlStatus?: string;
  fileDate?: Date;
  serialNumber?: string;
}

/**
 * Represents a patent case family association.
 *
 * @property accountId - The account identifier.
 * @property caseFamilyNm - The name of the case family.
 * @property caseId - The unique identifier for the case.
 * @property caseSuffix - The suffix of the case.
 * @property confirmationNumber - The confirmation number.
 * @property createDate - The date the case was created.
 * @property createdByUserId - The user ID who created the case.
 * @property expireDate - The expiration date.
 * @property genericField1 - Generic field 1.
 * @property genericField2 - Generic field 2.
 * @property genericField3 - Generic field 3.
 * @property genericField4 - Generic field 4.
 * @property genericField5 - Generic field 5.
 * @property genericField6 - Generic field 6.
 * @property issueDate - The issue date.
 * @property jurisdictionId - The jurisdiction identifier.
 * @property maintenanceStatus - The maintenance status.
 * @property modifiedByUserId - The user ID who last modified the case.
 * @property modifiedDate - The date the case was last modified.
 * @property nationalFilingDate - The national filing date.
 * @property patentApplicationType - The patent application type.
 * @property patentCaseType - The patent case type identifier.
 * @property patentNumber - The patent number.
 * @property patentStatus - The patent status.
 * @property primaryCaseNm - The primary name of the case.
 * @property priorityDate - The priority date.
 * @property statusDate - The status date.
 * @property title - The title of the case.
 * @property url - The URL for the case.
 */
export interface CaseFamilyAssociation extends FamilyAssociationBase {
  accountId?: number;
  caseFamilyNm?: string;
  caseId?: number;
  caseSuffix?: string;
  confirmationNumber?: string;
  createDate?: Date;
  createdByUserId?: number;
  expireDate?: Date;
  genericField1?: string;
  genericField2?: string;
  genericField3?: string;
  genericField4?: string;
  genericField5?: string;
  genericField6?: string;
  issueDate?: Date;
  jurisdictionId?: number;
  maintenanceStatus?: string;
  modifiedByUserId?: number;
  modifiedDate?: Date;
  nationalFilingDate?: Date;
  patentApplicationType?: string;
  patentCaseType?: number;
  patentNumber?: string;
  patentStatus?: string;
  primaryCaseNm?: string;
  priorityDate?: Date;
  statusDate?: Date;
  title?: string;
  url?: string;
}

/**
 * Represents a matter family association.
 *
 * @property matterFamilyNm - The name of the matter family.
 * @property matterId - The unique identifier for the matter.
 * @property matterNm - The matter name.
 * @property matterTitle - The title of the matter.
 */
export interface MatterFamilyAssociation extends FamilyAssociationBase {
  matterFamilyNm?: string;
  matterId?: number;
  matterNm?: string;
  matterTitle?: string;
}

/**
 * Represents a trademark family association.
 *
 * @property caseFamilyNm - The name of the case family.
 * @property companyCaseNm - The company case name.
 * @property trademarkId - The unique identifier for the trademark.
 * @property trademarkNm - The trademark name.
 */
export interface TrademarkFamilyAssociation extends FamilyAssociationBase {
  caseFamilyNm?: string;
  companyCaseNm?: string;
  trademarkId?: number;
  trademarkNm?: string;
}

/**
 * Represents the response received after uploading a trifold document.
 *
 * @property docSubTypeId - Optional. The subtype identifier of the document.
 * @property docZonedDateTime - Optional. The zoned date and time of the document.
 * @property documentDate - Optional. The date of the document as a number (timestamp).
 * @property exportControlDoc - Optional. The export control document information.
 * @property fileId - Optional. The unique identifier for the uploaded file.
 * @property fileName - Optional. The name of the uploaded file.
 * @property filePriority - Optional. The priority of the file.
 * @property fileSource - Optional. The source of the file.
 * @property hasAttachments - Optional. Indicates if the document has attachments.
 * @property receivedDate - Optional. The date the document was received as a number (timestamp).
 * @property title - Optional. The title of the document.
 * @property trifold - Optional. The trifold information.
 * @property uploadUserName - Optional. The username of the user who uploaded the document.
 */
export interface BaseTrifoldUploadResponse {
  docSubTypeId?: number;
  docZonedDateTime?: Date;
  documentDate?: number;
  exportControlDoc?: string;
  fileId?: number;
  fileName?: string;
  filePriority?: string;
  fileSource?: string;
  hasAttachments?: string;
  receivedDate?: number;
  title?: string;
  trifold?: string;
  uploadUserName?: string;
}

/**
 * Represents a file uploaded for a case, extending the base trifold upload response.
 *
 * @remarks
 * This interface adds an optional primary case name to the base upload response.
 *
 * @property primaryCaseNm - The name of the primary case associated with the uploaded file (optional).
 */
export interface CaseUploadFile extends BaseTrifoldUploadResponse {
  primaryCaseNm?: string;
}

/**
 * Represents a file uploaded for a trademark, extending the base trifold upload response.
 *
 * @remarks
 * This interface adds an optional company case name to the base upload response.
 *
 * @property companyCaseNm - (Optional) The name of the company case associated with the trademark upload.
 */
export interface TrademarkUploadFile extends BaseTrifoldUploadResponse {
  companyCaseNm?: string;
}

/**
 * Represents the response for a file upload related to a matter.
 * Extends {@link BaseTrifoldUploadResponse} to include additional matter-specific information.
 *
 * @property {string} [matterNm] - Optional name of the matter associated with the uploaded file.
 */
export interface MatterUploadFile extends BaseTrifoldUploadResponse {
  matterNm?: string;
}

/**
 * Represents a country with optional identifier and name properties.
 *
 * @property id - The unique identifier of the country.
 * @property name - The name of the country.
 */
export interface Country {
  id?: string;
  name?: string;
}

/**
 * Deleting a file note.
 *
 * @property caseNumber - (Optional) The case number associated with the file note.
 * @property fileId - (Optional) The unique identifier of the file.
 * @property fileNoteId - (Optional) The unique identifier of the file note to be deleted.
 */
export interface DeleteFileNote {
  caseNumber?: string;
  fileId?: number;
  fileNoteId?: number;
}

/**
 * Deleting a reference file note.
 *
 * @property {number} [fileId] - The unique identifier of the file.
 * @property {number} [fileNoteId] - The unique identifier of the file note to be deleted.
 */
export interface DeleteReferenceFileNote {
  fileId?: number;
  fileNoteId?: number;
}

/**
 * Represents a value that can be displayed to the user, typically used for dropdowns or selection lists.
 *
 * @property display - The human-readable string to display.
 * @property value - The underlying value associated with the display string.
 */
export interface DisplayableValueDO {
  display?: string;
  value?: string;
}

/**
 * Represents a subtype of a document with various properties indicating its usage and identification.
 *
 * @property agentTrifold - Optional. Trifold information specific to agents.
 * @property doctypeId - Optional. Unique identifier for the document type.
 * @property doctypeName - Optional. Name of the document type.
 * @property id - Optional. Unique identifier for the document subtype.
 * @property name - Optional. Name of the document subtype.
 * @property omTrifold - Optional. Trifold information specific to OM (Office Management).
 * @property patentTrifold - Optional. Trifold information specific to patents.
 * @property tmTrifold - Optional. Trifold information specific to trademarks.
 * @property trifold - Optional. General trifold information.
 * @property usedInAgent - Optional. Indicates if the subtype is used in agent-related processes.
 * @property usedInOm - Optional. Indicates if the subtype is used in OM-related processes.
 * @property usedInOmScanning - Optional. Indicates if the subtype is used in OM scanning.
 * @property usedInPatent - Optional. Indicates if the subtype is used in patent-related processes.
 * @property usedInPatentScanning - Optional. Indicates if the subtype is used in patent scanning.
 * @property usedInScanning - Optional. Indicates if the subtype is used in general scanning.
 * @property usedInTm - Optional. Indicates if the subtype is used in trademark-related processes.
 * @property usedInTmScanning - Optional. Indicates if the subtype is used in trademark scanning.
 * @property group - Optional. Group to which the document subtype belongs.
 */
export interface DocumentSubType {
  agentTrifold?: string;
  doctypeId?: number;
  doctypeName?: string;
  id?: number;
  name?: string;
  omTrifold?: string;
  patentTrifold?: string;
  tmTrifold?: string;
  trifold?: string;
  usedInAgent?: boolean;
  usedInOm?: boolean;
  usedInOmScanning?: boolean;
  usedInPatent?: boolean;
  usedInPatentScanning?: boolean;
  usedInScanning?: boolean;
  usedInTm?: boolean;
  usedInTmScanning?: boolean;
  group?: string;
}

/**
 * Document type with various properties.
 *
 * @property agentTrifold - Optional string representing the agent trifold.
 * @property id - Optional unique identifier for the document type.
 * @property name - Optional name of the document type.
 * @property usedInAgent - Optional flag indicating if used in agent context.
 * @property usedInOm - Optional flag indicating if used in OM context.
 * @property usedInPatent - Optional flag indicating if used in patent context.
 * @property usedInTm - Optional flag indicating if used in TM context.
 */
export interface DocumentType {
  agentTrifold?: string;
  id?: number;
  name?: string;
  usedInAgent?: boolean;
  usedInOm?: boolean;
  usedInPatent?: boolean;
  usedInTm?: boolean;
}

/**
 * Represents a duration of time with optional properties for nanoseconds, seconds,
 * sign (negative), units, and zero-value indication.
 *
 * @property nano - The number of nanoseconds in the duration.
 * @property negative - Indicates if the duration is negative.
 * @property seconds - The number of seconds in the duration.
 * @property units - An array of temporal units that describe the duration.
 * @property zero - Indicates if the duration is zero.
 */
export interface Duration {
  nano?: number;
  negative?: boolean;
  seconds?: number;
  units?: Array<TemporalUnit>;
  zero?: boolean;
}

/**
 * Represents a generic response structure from the EPS Web Service.
 *
 * @template T - The type of the response data.
 * @property {number} [code] - Optional response code indicating the status of the request.
 * @property {T} [data] - Optional payload containing the response data.
 * @property {unknown} [exception] - Optional exception information if an error occurred.
 * @property {string} [graceLogins] - Optional string representing grace login information.
 * @property {string} [message] - Optional message providing additional information about the response.
 * @property {EpsWsResponse.TypeEnum} [type] - Optional type enum indicating the response type.
 */
export interface EpsWsResponse<T> {
  code?: number;
  data?: T;
  exception?: unknown;
  graceLogins?: string;
  message?: string;
  type?: EpsWsResponse.TypeEnum;
}
export namespace EpsWsResponse {
  export type TypeEnum =
    | 'SUCCESS'
    | 'GENERAL'
    | 'DUPLICATE_FILE'
    | 'DUPLICATE_REFERENCE'
    | 'INVALID_PARAMETER'
    | 'EMPTY_FILE'
    | 'CASE_DOESNT_EXIST'
    | 'TRADEMARK_DOESNT_EXIST'
    | 'MATTER_DOESNT_EXIST'
    | 'PERMISSION_DENIED'
    | 'EXPORT_CONTROL'
    | 'DUPLICATE_CASE'
    | 'DUPLICATE_EXTERNAL_REF'
    | 'ALREADY_EXISTS_IN_TRASHBIN'
    | 'FILE_IS_LOCKED'
    | 'CITATION_EXISTS'
    | 'SHARED_WITH_USER'
    | 'USED_BY_ACTIVE_WORKFLOW'
    | 'FILE_IS_CHECKED_OUT'
    | 'FILE_DOESNT_EXIST'
    | 'CAN_NOT_UPLOAD_FILES_TO_TRASHBIN'
    | 'FILE_NAME_MUST_MATCH'
    | 'FILE_EXT_MUST_MATCH'
    | 'FILE_EXTENSION_INACTIVE'
    | 'DUPLICATE_KEYWORD'
    | 'KEYWORD_DOES_NOT_EXIST'
    | 'FILE_NOTE_DOES_NOT_EXIST'
    | 'GROUP_DOES_NOT_EXIST'
    | 'DUPLICATE_GROUP'
    | 'FILE_NOT_CHECKED_OUT'
    | 'ALREADY_EXISTS_IN_TRIFOLD'
    | 'WORKFLOW_DOESNT_EXIST'
    | 'WORKFLOW_TASK_DOESNT_EXIST'
    | 'INVALID_RENUMBER_STATE'
    | 'CHANGE_NOT_ALLOWED';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const TypeEnum = {
    SUCCESS: 'SUCCESS' as TypeEnum,
    GENERAL: 'GENERAL' as TypeEnum,
    DUPLICATEFILE: 'DUPLICATE_FILE' as TypeEnum,
    DUPLICATEREFERENCE: 'DUPLICATE_REFERENCE' as TypeEnum,
    INVALIDPARAMETER: 'INVALID_PARAMETER' as TypeEnum,
    EMPTYFILE: 'EMPTY_FILE' as TypeEnum,
    CASEDOESNTEXIST: 'CASE_DOESNT_EXIST' as TypeEnum,
    TRADEMARKDOESNTEXIST: 'TRADEMARK_DOESNT_EXIST' as TypeEnum,
    MATTERDOESNTEXIST: 'MATTER_DOESNT_EXIST' as TypeEnum,
    PERMISSIONDENIED: 'PERMISSION_DENIED' as TypeEnum,
    EXPORTCONTROL: 'EXPORT_CONTROL' as TypeEnum,
    DUPLICATECASE: 'DUPLICATE_CASE' as TypeEnum,
    DUPLICATEEXTERNALREF: 'DUPLICATE_EXTERNAL_REF' as TypeEnum,
    ALREADYEXISTSINTRASHBIN: 'ALREADY_EXISTS_IN_TRASHBIN' as TypeEnum,
    FILEISLOCKED: 'FILE_IS_LOCKED' as TypeEnum,
    CITATIONEXISTS: 'CITATION_EXISTS' as TypeEnum,
    SHAREDWITHUSER: 'SHARED_WITH_USER' as TypeEnum,
    USEDBYACTIVEWORKFLOW: 'USED_BY_ACTIVE_WORKFLOW' as TypeEnum,
    FILEISCHECKEDOUT: 'FILE_IS_CHECKED_OUT' as TypeEnum,
    FILEDOESNTEXIST: 'FILE_DOESNT_EXIST' as TypeEnum,
    CANNOTUPLOADFILESTOTRASHBIN: 'CAN_NOT_UPLOAD_FILES_TO_TRASHBIN' as TypeEnum,
    FILENAMEMUSTMATCH: 'FILE_NAME_MUST_MATCH' as TypeEnum,
    FILEEXTMUSTMATCH: 'FILE_EXT_MUST_MATCH' as TypeEnum,
    FILEEXTENSIONINACTIVE: 'FILE_EXTENSION_INACTIVE' as TypeEnum,
    DUPLICATEKEYWORD: 'DUPLICATE_KEYWORD' as TypeEnum,
    KEYWORDDOESNOTEXIST: 'KEYWORD_DOES_NOT_EXIST' as TypeEnum,
    FILENOTEDOESNOTEXIST: 'FILE_NOTE_DOES_NOT_EXIST' as TypeEnum,
    GROUPDOESNOTEXIST: 'GROUP_DOES_NOT_EXIST' as TypeEnum,
    DUPLICATEGROUP: 'DUPLICATE_GROUP' as TypeEnum,
    FILENOTCHECKEDOUT: 'FILE_NOT_CHECKED_OUT' as TypeEnum,
    ALREADYEXISTSINTRIFOLD: 'ALREADY_EXISTS_IN_TRIFOLD' as TypeEnum,
    WORKFLOWDOESNTEXIST: 'WORKFLOW_DOESNT_EXIST' as TypeEnum,
    WORKFLOWTASKDOESNTEXIST: 'WORKFLOW_TASK_DOESNT_EXIST' as TypeEnum,
    INVALIDRENUMBERSTATE: 'INVALID_RENUMBER_STATE' as TypeEnum,
    CHANGENOTALLOWED: 'CHANGE_NOT_ALLOWED' as TypeEnum,
  };
}

/**
 * Wrapper interface for an EPS Web Service response.
 *
 * @template T - The type of the data contained in the EPS Web Service response.
 * @property {EpsWsResponse<T>} [epsws] - The optional EPS Web Service response object.
 */
export interface EpsWsWrapper<T> {
  epsws?: EpsWsResponse<T>;
}

/**
 * Represents the association between a file and an event in the docket system.
 *
 * @property eventId - The unique identifier of the event.
 * @property fileId - The unique identifier of the file.
 */
export interface FileDocketEventsId {
  eventId?: string;
  fileId?: number;
}

/**
 * Represents the events associated with a file docket, including creation and modification details.
 *
 * @property createDate - The date when the event was created.
 * @property createdByUserId - The user ID of the creator of the event.
 * @property displayName - The display name associated with the event.
 * @property eventUrl - The URL related to the event.
 * @property id - The unique identifier for the file docket event.
 * @property modifiedByUserId - The user ID of the person who last modified the event.
 * @property modifiedDate - The date when the event was last modified.
 */
export interface FileDocketEvents {
  createDate?: Date;
  createdByUserId?: number;
  displayName?: string;
  eventUrl?: string;
  id?: FileDocketEventsId;
  modifiedByUserId?: number;
  modifiedDate?: Date;
}

/**
 * Represents an event related to a file in the FTF API.
 *
 * @property caseNumber - (Optional) The case number associated with the file event.
 * @property eventId - (Optional) The unique identifier for the event.
 * @property fileId - (Optional) The unique identifier for the file.
 */
export interface FileEvent {
  caseNumber?: string;
  eventId?: string;
  fileId?: number;
}

/**
 * Represents a keyword associated with a file in the FTF API.
 *
 * @property caseNumber - (Optional) The case number related to the file.
 * @property fileId - (Optional) The unique identifier of the file.
 * @property keywordText - (Optional) The keyword text associated with the file.
 */
export interface FileKeyword {
  caseNumber?: string;
  fileId?: number;
  keywordText?: string;
}

/**
 * Represents a note associated with a file in the FTF API.
 * @property {string} [caseNumber] - The case number associated with the file note.
 * @property {number} [fileId] - The unique identifier of the file.
 * @property {string} [fileNote] - The content of the file note.
 */
export interface FileNote {
  caseNumber?: string;
  fileId?: number;
  fileNote?: string;
}

/**
 * Represents the priority of a file.
 *
 * @property {string} [id] - The unique identifier of the file priority.
 * @property {string} [name] - The name or description of the file priority.
 */
export interface FilePriority {
  id?: string;
  name?: string;
}

/**
 * Represents a file reference in the FTF API.
 *
 * @property {Date} [documentDate] - The date of the document.
 * @property {number} [fileId] - The unique identifier of the file.
 * @property {string} [fileName] - The name of the file.
 * @property {string} [fullFileName] - The full name of the file.
 * @property {string} [title] - The title of the file.
 * @property {string} [type] - The type of the file.
 */
export interface FileReference {
  documentDate?: Date;
  fileId?: number;
  fileName?: string;
  fullFileName?: string;
  title?: string;
  type?: string;
}

/**
 * Represents a file version in the FTF API.
 *
 * @property {number} [versionNumber] - The version number of the file.
 * @property {number} [createDate] - The creation date of the file version.
 * @property {string} [createdBy] - The user who created the file version.
 * @property {number} [fileId] - The unique identifier of the file associated with this version.
 */
export interface FileVersionDO {
  versionNumber?: number;
  createDate?: number;
  createdBy?: string;
  fileId?: number;
}

/**
 * Represents a file in the FTF API.
 *
 * @property {string} [quickSearch] - A quick search string for filtering files.
 * @property {string} sortBy - The field by which to sort the files.
 * @property {'asc' | 'desc'} sortOrder - The order in which to sort the files (ascending or descending).
 * @property {number} currentIndex - The current index for pagination.
 * @property {number} displayNumber - The number of files to display per page.
 *
 */
export interface GridRequest {
  quickSearch?: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  currentIndex: number;
  displayNumber: number;
}

/**
 * Represents the criteria and results for a grid-based data query.
 *
 * @template T The type of the items in the results array.
 * @property {string} [active] - The active status or identifier for the grid.
 * @property {boolean} [administrator] - Indicates if the user has administrator privileges.
 * @property {number} [currentPage] - The current page number in the grid.
 * @property {number} displayNumber - The number of items to display per page.
 * @property {number} [endIndex] - The ending index of the current page.
 * @property {number} [maxRow] - The maximum row index in the grid.
 * @property {number} [minRow] - The minimum row index in the grid.
 * @property {Array<T>} results - The array of results for the current grid query.
 * @property {string} [search] - The search query string.
 * @property {string} [sortBy] - The field by which to sort the results.
 * @property {string} [sortOrder] - The order in which to sort the results (e.g., 'asc' or 'desc').
 * @property {number} startIndex - The starting index of the current page.
 * @property {number} [totalPages] - The total number of pages available.
 * @property {number} totalRows - The total number of rows available.
 * @property {string} [utc] - The UTC timestamp or identifier for the grid data.
 */
export interface GridCriteria<T> {
  active?: string;
  administrator?: boolean;
  currentPage?: number;
  displayNumber: number;
  endIndex?: number;
  maxRow?: number;
  minRow?: number;
  results: Array<T>;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  startIndex: number;
  totalPages?: number;
  totalRows: number;
  utc?: string;
}

/**
 * Data Transfer Object representing a group.
 *
 * @property {string} [description] - Optional description of the group.
 * @property {number} [groupId] - Optional unique identifier for the group.
 * @property {string} [title] - Optional title of the group.
 */
export interface GroupsDTO {
  description?: string;
  groupId?: number;
  title?: string;
}

/**
 * Represents a response containing an image, including its Base64 encoded data and content type.
 *
 * @property data - The Base64 encoded image string.
 * @property type - The content type of the image (e.g., "image/png").
 */
export interface ImageResponse {
  /**
   * The Base64 Encoded Image
   */
  data?: string;
  /**
   * The Content Type
   */
  type?: string;
}

/**
 * Represents a jurisdiction entity with optional identifier and name.
 *
 * @property id - The unique identifier of the jurisdiction.
 * @property name - The name of the jurisdiction.
 */
export interface Jurisdiction {
  id?: number;
  name?: string;
}

/**
 * Represents the status of a maintenance operation.
 *
 * @property id - The unique identifier of the maintenance status.
 * @property name - The display name of the maintenance status.
 */
export interface MaintenanceStatus {
  id?: string;
  name?: string;
}

/**
 * Represents a type of mark with optional identifier and name.
 *
 * @property {string} [id] - The unique identifier for the mark type.
 * @property {string} [name] - The display name of the mark type.
 */
export interface MarkType {
  id?: string;
  name?: string;
}

/**
 * Matter Details
 * @property {string} [attorneys] - The attorneys associated with the matter.
 * @property {string} [caseUrl] - The URL of the case.
 * @property {string} [catGenericField1] - A generic field for categorization.
 * @property {string} [clientName] - The name of the client.
 * @property {string} [clientReference] - The client reference for the matter.
 * @property {string} [country] - The country associated with the matter.
 * @property {string} [createByUser] - The user who created the matter.
 * @property {Date} [createDate] - The date the matter was created.
 * @property {Date} [expireDate] - The expiration date of the matter.
 * @property {string} [exportControlStatus] - The export control status of the matter.
 * @property {string} [externalRef] - An external reference for the matter.
 * @property {string} [filingDate] - The filing date of the matter.
 * @property {Date} [genericDate1] - A generic date field for custom use.
 * @property {Date} [genericDate2] - A generic date field for custom use.
 * @property {Date} [genericDate3] - A generic date field for custom use.
 * @property {Date} [genericDate4] - A generic date field for custom use.
 * @property {Date} [genericDate5] - A generic date field for custom use.
 * @property {Date} [genericDate6] - A generic date field for custom use.
 * @property {string} [genericField1] - A generic field for custom use.
 * @property {string} [genericField2] - A generic field for custom use.
 * @property {string} [genericField3] - A generic field for custom use.
 * @property {string} [genericField4] - A generic field for custom use.
 * @property {string} [genericField5] - A generic field for custom use.
 * @property {string} [genericField6] - A generic field for custom use.
 * @property {string} [groups] - The groups associated with the matter.
 * @property {string} [matterFamilyNm] - The family name of the matter.
 * @property {number} [matterId] - The unique identifier for the matter.
 * @property {string} [matterNm] - The name of the matter.
 * @property {string} [matterSuffix] - The suffix of the matter.
 * @property {string} [matterTitle] - The title of the matter.
 * @property {string} [matterType] - The type of the matter.
 * @property {string} [modifiedByUser] - The user who last modified the matter.
 * @property {Date} [modifiedDate] - The date the matter was last modified.
 * @property {string} [outsideAttorneys] - The outside attorneys associated with the matter.
 * @property {string} [outsideCounselDocketNm] - The outside counsel docket name.
 * @property {string} [ownerName] - The name of the owner of the matter.
 * @property {string} [priorityCountry] - The priority country of the matter.
 * @property {Date} [priorityDate] - The priority date of the matter.
 * @property {string} [priorityNumber] - The priority number of the matter.
 * @property {string} [products] - The products associated with the matter.
 * @property {Date} [registrationDate] - The registration date of the matter.
 * @property {string} [registrationNumber] - The registration number of the matter.
 * @property {string} [serialNumber] - The serial number of the matter.
 * @property {string} [status] - The status of the matter.
 * @property {Date} [statusDate] - The date when the status was last updated.
 * @property {string} [technologies] - The technologies associated with the matter.
 */
export interface MatterDetails {
  attorneys?: string;
  caseUrl?: string;
  catGenericField1?: string;
  clientName?: string;
  clientReference?: string;
  country?: string;
  createByUser?: string;
  createDate?: Date;
  expireDate?: Date;
  exportControlStatus?: string;
  externalRef?: string;
  filingDate?: string;
  genericDate1?: Date;
  genericDate2?: Date;
  genericDate3?: Date;
  genericDate4?: Date;
  genericDate5?: Date;
  genericDate6?: Date;
  genericField1?: string;
  genericField2?: string;
  genericField3?: string;
  genericField4?: string;
  genericField5?: string;
  genericField6?: string;
  groups?: string;
  matterFamilyNm?: string;
  matterId?: number;
  matterNm?: string;
  matterSuffix?: string;
  matterTitle?: string;
  matterType?: string;
  modifiedByUser?: string;
  modifiedDate?: Date;
  outsideAttorneys?: string;
  outsideCounselDocketNm?: string;
  ownerName?: string;
  priorityCountry?: string;
  priorityDate?: Date;
  priorityNumber?: string;
  products?: string;
  registrationDate?: Date;
  registrationNumber?: string;
  serialNumber?: string;
  status?: string;
  statusDate?: Date;
  technologies?: string;
}

/**
 * Represents a matter data object with optional properties related to export control, identification, and metadata.
 *
 * @property {string} [country] - The country associated with the matter.
 * @property {string} [exportControlStatus] - The export control status of the matter.
 * @property {Date} [fileDate] - The date the matter was filed.
 * @property {string} [matterFamilyNm] - The family name of the matter.
 * @property {number} [matterId] - The unique identifier for the matter.
 * @property {string} [matterNm] - The name of the matter.
 * @property {string} [matterTitle] - The title of the matter.
 * @property {string} [serialNumber] - The serial number associated with the matter.
 */
export interface MatterDO {
  country?: string;
  exportControlStatus?: string;
  fileDate?: Date;
  matterFamilyNm?: string;
  matterId?: number;
  matterNm?: string;
  matterTitle?: string;
  serialNumber?: string;
}

/**
 * Matter Case Link Response
 *
 * @property {string} [caseUrl] - The URL of the case.
 * @property {string} [matterNm] - The name of the matter.
 */
export interface MatterLink {
  caseUrl?: string;
  matterNm?: string;
}

/**
 * Matter Status
 * @property {string} [id] - The unique identifier of the matter status.
 * @property {string} [name] - The display name of the matter status.
 */
export interface MatterStatus {
  id?: string;
  name?: string;
}

/**
 * Matter Trifold Data
 *
 * @property {Array<WsFile>} [correspondence] - An array of correspondence files associated with the matter.
 * @property {Array<WsFile>} [documents] - An array of document files associated with the matter.
 * @property {Array<WsFile>} [filing] - An array of filing files associated with the matter.
 * @property {string} [matterNm] - The name of the matter.
 */
export interface MatterTrifoldData {
  correspondence?: Array<WsFile>;
  documents?: Array<WsFile>;
  filing?: Array<WsFile>;
  matterNm?: string;
}

/**
 * Represents a matter data object with optional properties related to export control, identification, and metadata.
 *
 * @property {number} [matterId] - The unique identifier for the matter.
 * @property {string} [matterNm] - The name of the matter.
 */
export interface Matter {
  matterId?: number;
  matterNm?: string;
}

/**
 * Matter Type
 * @property {string} [id] - The unique identifier of the matter type.
 * @property {string} [name] - The display name of the matter type.
 */
export interface MatterType {
  id?: string;
  name?: string;
}

/**
 * Matter Upload
 *
 * @property {number} [fileId] - The unique identifier for the uploaded file.
 * @property {string} [matterNm] - The name of the matter associated with the uploaded file.
 * @property {string} [uploadUserName] - The username of the user who uploaded the file.
 */
export interface MatterUpload {
  fileId?: number;
  matterNm?: string;
  uploadUserName?: string;
}

/**
 * Patent Case Type
 * @property {number} [id] - The unique identifier of the patent case type.
 * @property {string} [name] - The display name of the patent case type.
 */
export interface PatentCaseType {
  id?: number;
  name?: string;
}

/**
 * Patent data object with optional properties.
 * @property {string} [caseFamilyNm] - The family name of the patent case.
 * @property {number} [caseId] - The unique identifier for the patent case.
 * @property {string} [country] - The country associated with the patent case.
 * @property {string} [exportControlStatus] - The export control status of the patent case.
 * @property {Date} [fileDate] - The date the patent case was filed.
 * @property {string} [primaryCaseNm] - The primary name of the patent case.
 * @property {string} [serialNumber] - The serial number associated with the patent case.
 * @property {string} [title] - The title of the patent case.
 */
export interface PatentDO {
  caseFamilyNm?: string;
  caseId?: number;
  country?: string;
  exportControlStatus?: string;
  fileDate?: Date;
  primaryCaseNm?: string;
  serialNumber?: string;
  title?: string;
}

/**
 * Patent Case Link
 * @property {string} [caseUrl] - The URL of the patent case.
 * @property {string} [primaryCaseNm] - The primary name of the patent case.
 */
export interface PatentLink {
  caseUrl?: string;
  primaryCaseNm?: string;
}

/**
 * Patent Trifold Data
 *
 * @property {Array<WsFile>} [correspondence] - An array of correspondence files associated with the patent.
 * @property {Array<WsFile>} [documents] - An array of document files associated with the patent.
 * @property {Array<WsFile>} [filing] - An array of filing files associated with the patent.
 * @property {string} [primaryCaseNm] - The primary name of the patent case.
 */
export interface PatentTrifoldData {
  correspondence?: Array<WsFile>;
  documents?: Array<WsFile>;
  filing?: Array<WsFile>;
  primaryCaseNm?: string;
}

/**
 * Represents a patent data object with optional properties.
 *
 * @property {number} [caseId] - The unique identifier for the patent case.
 * @property {string} [primaryCaseNm] - The primary name associated with the patent case.
 */
export interface Patent {
  caseId?: number;
  primaryCaseNm?: string;
}

/**
 * Patent Upload
 * @property {number} [fileId] - The unique identifier for the uploaded file.
 * @property {string} [primaryCaseNm] - The primary name of the patent case associated with the uploaded file.
 * @property {string} [uploadUserName] - The username of the user who uploaded the file.
 */
export interface PatentUpload {
  fileId?: number;
  primaryCaseNm?: string;
  uploadUserName?: string;
}

/**
 * Publication
 * @property {string} [countryCode] - The country code associated with the publication.
 * @property {string} [kindCode] - The kind code of the publication.
 * @property {string} [name] - The name of the publication.
 */
export interface Publication {
  countryCode?: string;
  kindCode?: string;
  name?: string;
}

/**
 * Reference Details
 * @property {string} [applicant] - The applicant of the reference.
 * @property {string} [applicantNumber] - The applicant number of the reference.
 * @property {string} [articleDate] - The article date of the reference.
 * @property {string} [articleTitle] - The article title of the reference.
 * @property {string} [assignee] - The assignee of the reference.
 * @property {string} [author] - The author of the reference.
 * @property {string} [city] - The city associated with the reference.
 * @property {string} [countryCode] - The country code of the reference.
 * @property {Date} [createDate] - The creation date of the reference.
 * @property {string} [createdByUser] - The user who created the reference.
 * @property {string} [customCitation] - The custom citation of the reference.
 * @property {string} [fileAvailable] - Indicates if a file is available for the reference.
 * @property {number} [fileId] - The unique identifier for the file associated with the reference.
 * @property {string} [fileName] - The name of the file associated with the reference.
 * @property {string} [fileNameDisplay] - The display name of the file associated with the reference.
 * @property {number} [fileSize] - The size of the file associated with the reference.
 * @property {string} [fileSource] - The source of the file associated with the reference.
 * @property {string} [inventors] - The inventors associated with the reference.
 * @property {Date} [issueDate] - The issue date of the reference.
 * @property {string} [itemTitle] - The item title of the reference.
 * @property {string} [kindCode] - The kind code of the reference.
 * @property {string} [location] - The location associated with the reference.
 * @property {string} [modifiedByUser] - The user who last modified the reference.
 * @property {Date} [modifiedDate] - The date when the reference was last modified.
 * @property {string} [pages] - The pages associated with the reference.
 * @property {string} [patentNumber] - The patent number of the reference.
 * @property {Date} [publicationDate] - The publication date of the reference.
 * @property {string} [publicationNumber] - The publication number of the reference.
 * @property {string} [publisher] - The publisher of the reference.
 * @property {number} [referenceType] - The type of the reference.
 * @property {string} [referenceTypeDisplay] - The display name of the reference type.
 * @property {boolean} [requirementsOverride] - Indicates if requirements are overridden for the reference.
 * @property {string} [titleDisplay] - The display title of the reference.
 * @property {boolean} [useCustomCitation] - Indicates if a custom citation is used for the reference.
 * @property {string} [volumeNumber] - The volume number of the reference.
 */
export interface ReferenceDetails {
  applicant?: string;
  applicantNumber?: string;
  articleDate?: string;
  articleTitle?: string;
  assignee?: string;
  author?: string;
  city?: string;
  countryCode?: string;
  createDate?: Date;
  createdByUser?: string;
  customCitation?: string;
  fileAvailable?: string;
  fileId?: number;
  fileName?: string;
  fileNameDisplay?: string;
  fileSize?: number;
  fileSource?: string;
  inventors?: string;
  issueDate?: Date;
  itemTitle?: string;
  kindCode?: string;
  location?: string;
  modifiedByUser?: string;
  modifiedDate?: Date;
  pages?: string;
  patentNumber?: string;
  publicationDate?: Date;
  publicationNumber?: string;
  publisher?: string;
  referenceType?: number;
  referenceTypeDisplay?: string;
  requirementsOverride?: boolean;
  titleDisplay?: string;
  useCustomCitation?: boolean;
  volumeNumber?: string;
}

/**
 * Reference File Keyword
 * @property {number} [fileId] - The unique identifier of the file.
 * @property {string} [keywordText] - The keyword text associated with the file.
 */
export interface ReferenceFileKeyword {
  fileId?: number;
  keywordText?: string;
}

/**
 * Reference File Note
 * @property {number} [fileId] - The unique identifier of the file.
 * @property {string} [fileNote] - The content of the file note.
 */
export interface ReferenceFileNote {
  fileId?: number;
  fileNote?: string;
}

/**
 * Reference Source
 * @property {number} [id] - The unique identifier of the reference source.
 * @property {string} [name] - The name of the reference source.
 */
export interface ReferenceSource {
  id?: number;
  name?: string;
}

/**
 * Reference Type
 * @property {string} [id] - The unique identifier of the reference type.
 * @property {string} [name] - The name of the reference type.
 */
export interface ReferenceType {
  id?: string;
  name?: string;
}

/**
 * Reference Version Upload Response
 * @property {number} [fileId] - The unique identifier for the uploaded file.
 * @property {string} [uploadUserName] - The username of the user who uploaded the file.
 */
export interface ReferenceUploadDO {
  fileId?: number;
  uploadUserName?: string;
}

/**
 * Ref Upload File
 * @property {RefUploadFile.AssocTypeEnum} [assocType] - The type of association for the uploaded file.
 * @property {string} [assocValue] - The value associated with the uploaded file.
 * @property {Array<string>} [assocValues] - An array of values associated with the uploaded file.
 * @property {Date} [docZonedDateTime] - The date and time associated with the document.
 * @property {number} [documentDate] - The date of the document.
 * @property {string} [exportControlDoc] - The export control document associated with the file.
 * @property {number} [fileId] - The unique identifier of the file.
 * @property {string} [fileName] - The name of the file.
 * @property {string} [fileSource] - The source of the file.
 * @property {number} [receivedDate] - The date the file was received.
 * @property {string} [referenceType] - The type of reference associated with the file.
 * @property {Date} [sourceDate] - The source date associated with the file.
 * @property {number} [sourceId] - The unique identifier of the source associated with the file.
 * @property {string} [title] - The title of the file.
 * @property {string} [uploadUserName] - The username of the user who uploaded the file.
 */
export interface RefUploadFile {
  assocType?: RefUploadFile.AssocTypeEnum;
  assocValue?: string;
  assocValues?: Array<string>;
  docZonedDateTime?: Date;
  documentDate?: number;
  exportControlDoc?: string;
  fileId?: number;
  fileName?: string;
  fileSource?: string;
  receivedDate?: number;
  referenceType?: string;
  sourceDate?: Date;
  sourceId?: number;
  title?: string;
  uploadUserName?: string;
}

/**
 * Ref Upload File Assoc Type Enum
 * @enum {string} AssocTypeEnum
 * @property {string} PrimaryCaseNm - Represents the primary case name association type.
 */
export namespace RefUploadFile {
  export type AssocTypeEnum = 'primaryCaseNm';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const AssocTypeEnum = {
    PrimaryCaseNm: 'primaryCaseNm' as AssocTypeEnum,
  };
}

/**
 * Represents the response for a reference file upload, extending the base trifold upload response.
 *
 * @extends BaseTrifoldUploadResponse
 * @property {string} [uploadUserName] - Optional username of the user who uploaded the file.
 * @property {string} fileName - Name of the uploaded file.
 */
export interface RefFileResponse extends BaseTrifoldUploadResponse {
  uploadUserName?: string;
  fileName: string;
}

/**
 * Simple User Data Object
 * @property {string} [firstName] - The first name of the user.
 * @property {number} [id] - The unique identifier of the user.
 * @property {string} [lastName] - The last name of the user.
 */
export interface SimpleUserDO {
  firstName?: string;
  id?: number;
  lastName?: string;
}

/**
 * Trademark State Registration
 * @property {string} [id] - The two-character state code.
 * @property {string} [name] - The full name of the state.
 */
export interface StateRegistration {
  /**
   * Two Character State Code
   */
  id?: string;
  /**
   * Full name of state
   */
  name?: string;
}

/**
 * Temporal Unit
 * @property {boolean} [dateBased] - Indicates if the temporal unit is date-based.
 * @property {Duration} [duration] - The duration associated with the temporal unit.
 * @property {boolean} [durationEstimated] - Indicates if the duration is estimated.
 * @property {boolean} [timeBased] - Indicates if the temporal unit is time-based.
 */
export interface TemporalUnit {
  dateBased?: boolean;
  duration?: Duration;
  durationEstimated?: boolean;
  timeBased?: boolean;
}

/**
 * Trademark Application Type
 * @property {string} [id] - The unique identifier of the trademark application type.
 * @property {string} [name] - The display name of the trademark application type.
 */
export interface TrademarkApplicationType {
  id?: string;
  name?: string;
}

/**
 * Trademark Case Type
 * @property {number} [id] - The unique identifier of the trademark case type.
 * @property {string} [name] - The display name of the trademark case type.
 */
export interface TrademarkCaseType {
  id?: number;
  name?: string;
}

/**
 * Trademark Details
 * @property {string} [assignee] - The assignee of the trademark.
 * @property {string} [attorney] - The attorney associated with the trademark.
 * @property {string} [caseFamily] - The family name of the trademark case.
 * @property {string} [caseSuffix] - The suffix of the trademark case.
 * @property {string} [caseType] - The type of the trademark case.
 * @property {string} [caseUrl] - The URL of the trademark case.
 * @property {string} [catGenericField1] - A generic field for categorization.
 * @property {string} [clientName] - The name of the client associated with the trademark.
 * @property {string} [clientReference] - The client reference for the trademark.
 * @property {string} [companyCaseNm] - The company case name associated with the trademark.
 * @property {string} [country] - The country associated with the trademark.
 * @property {string} [createByUser] - The user who created the trademark record.
 * @property {Date} [createDate] - The date the trademark record was created.
 * @property {string} [depictionMark] - The depiction of the trademark mark.
 * @property {string} [displayName] - The display name of the trademark.
 * @property {Date} [expireDate] - The expiration date of the trademark.
 * @property {string} [exportControlStatus] - The export control status of the trademark.
 * @property {string} [externalRef] - An external reference for the trademark.
 * @property {string} [filingBasis] - The filing basis of the trademark.
 * @property {string} [filingDate] - The filing date of the trademark.
 * @property {string} [filingJurisdicton] - The filing jurisdiction of the trademark.
 * @property {Date} [firstUseCommerce] - The date of first use in commerce for the trademark.
 * @property {Date} [firstUseDate] - The date of first use for the trademark.
 * @property {string} [genericField1] - A generic field for custom use.
 * @property {string} [genericField2] - A generic field for custom use.
 * @property {string} [genericField3] - A generic field for custom use.
 * @property {string} [genericField4] - A generic field for custom use.
 * @property {string} [genericField5] - A generic field for custom use.
 * @property {string} [genericField6] - A generic field for custom use.
 * @property {string} [goodAndServicesClasses] - The goods and services classes associated with the trademark.
 * @property {string} [group] - The group associated with the trademark.
 * @property {Date} [lastRenewalDate] - The date of the last renewal for the trademark.
 * @property {string} [markDrawingCode] - The mark drawing code of the trademark.
 * @property {Date} [modifiedDate] - The date the trademark record was last modified.
 * @property {string} [modifyByUser] - The user who last modified the trademark record.
 * @property {Date} [nextRenewalDate] - The date of the next renewal for the trademark.
 * @property {string} [outsideAttorney] - The outside attorney associated with the trademark.
 * @property {string} [ownerName] - The name of the owner of the trademark.
 * @property {string} [priorityCountry] - The priority country of the trademark.
 * @property {Date} [priorityDate] - The priority date of the trademark.
 * @property {string} [priorityNumber] - The priority number of the trademark.
 * @property {string} [proOutsideDocketNm] - The professional outside docket name associated with the trademark.
 * @property {string} [products] - The products associated with the trademark.
 * @property {string} [regType] - The registration type of the trademark.
 * @property {Date} [registrationDate] - The registration date of the trademark.
 * @property {string} [registrationNumber] - The registration number of the trademark.
 * @property {string} [serialNumber] - The serial number of the trademark.
 * @property {string} [standardCharacterClaim] - The standard character claim of the trademark.
 * @property {string} [state] - The state associated with the trademark.
 * @property {string} [status] - The status of the trademark.
 * @property {Date} [statusDate] - The date when the status was last updated.
 * @property {string} [subStatus] - The sub-status of the trademark.
 * @property {Date} [subStatusDate] - The date when the sub-status was last updated.
 * @property {string} [technologies] - The technologies associated with the trademark.
 * @property {number} [trademarkId] - The unique identifier for the trademark.
 * @property {string} [trademarkNm] - The name of the trademark.
 */
export interface TrademarkDetails {
  assignee?: string;
  attorney?: string;
  caseFamily?: string;
  caseSuffix?: string;
  caseType?: string;
  caseUrl?: string;
  catGenericField1?: string;
  clientName?: string;
  clientReference?: string;
  companyCaseNm?: string;
  country?: string;
  createByUser?: string;
  createDate?: Date;
  depictionMark?: string;
  displayName?: string;
  expireDate?: Date;
  exportControlStatus?: string;
  externalRef?: string;
  filingBasis?: string;
  filingDate?: string;
  filingJurisdicton?: string;
  firstUseCommerce?: Date;
  firstUseDate?: Date;
  genericField1?: string;
  genericField2?: string;
  genericField3?: string;
  genericField4?: string;
  genericField5?: string;
  genericField6?: string;
  goodAndServicesClasses?: string;
  group?: string;
  lastRenewalDate?: Date;
  markDrawingCode?: string;
  modifiedDate?: Date;
  modifyByUser?: string;
  nextRenewalDate?: Date;
  outsideAttorney?: string;
  ownerName?: string;
  priorityCountry?: string;
  priorityDate?: Date;
  priorityNumber?: string;
  proOutsideDocketNm?: string;
  products?: string;
  regType?: string;
  registrationDate?: Date;
  registrationNumber?: string;
  serialNumber?: string;
  standardCharacterClaim?: string;
  state?: string;
  status?: string;
  statusDate?: Date;
  subStatus?: string;
  subStatusDate?: Date;
  technologies?: string;
  trademarkId?: number;
  trademarkNm?: string;
}

/**
 * Trademark data object with optional properties.
 * @property {string} [caseFamilyNm] - The family name of the trademark case.
 * @property {string} [companyCaseNm] - The company case name associated with the trademark.
 * @property {string} [country] - The country associated with the trademark.
 * @property {string} [exportControlStatus] - The export control status of the trademark.
 * @property {Date} [fileDate] - The date the trademark case was filed.
 * @property {string} [serialNumber] - The serial number associated with the trademark case.
 * @property {number} [trademarkId] - The unique identifier for the trademark.
 * @property {string} [trademarkNm] - The name of the trademark.
 */
export interface TrademarkDO {
  caseFamilyNm?: string;
  companyCaseNm?: string;
  country?: string;
  exportControlStatus?: string;
  fileDate?: Date;
  serialNumber?: string;
  trademarkId?: number;
  trademarkNm?: string;
}

/**
 * Trademark Drawing Code
 * @property {string} [id] - The unique identifier of the trademark drawing code.
 * @property {string} [name] - The display name of the trademark drawing code.
 */
export interface TrademarkDrawingCode {
  id?: string;
  name?: string;
}

/**
 * Trademark Filing Basis
 * @property {string} [id] - The unique identifier of the trademark filing basis.
 * @property {string} [name] - The display name of the trademark filing basis.
 */
export interface TrademarkFilingBasis {
  id?: string;
  name?: string;
}

/**
 * Trademark Filing Jurisdiction
 * @property {string} [id] - The unique identifier of the trademark filing jurisdiction.
 * @property {string} [name] - The display name of the trademark filing jurisdiction.
 */
export interface TrademarkFilingJurisdiction {
  id?: string;
  name?: string;
}

/**
 * File Trademark Image Upload Response
 * @property {string} [companyCaseNm] - The company case name associated with the trademark image.
 * @property {string} [imageFileName] - The name of the uploaded trademark image file.
 * @property {string} [uploadUserName] - The username of the user who uploaded the trademark image.
 */
export interface TrademarkImageUpload {
  companyCaseNm?: string;
  imageFileName?: string;
  uploadUserName?: string;
}

/**
 * Trademark Case Link Response
 * @property {string} [caseUrl] - The URL of the trademark case.
 * @property {string} [companyCaseNm] - The company case name associated with the trademark.
 */
export interface TrademarkLink {
  caseUrl?: string;
  companyCaseNm?: string;
}

/**
 * Trademark Status
 * @property {string} [id] - The unique identifier of the trademark status.
 * @property {string} [name] - The display name of the trademark status.
 */
export interface TrademarkStatus {
  id?: string;
  name?: string;
}

/**
 * Trademark Trifold Data
 * @property {Array<WsFile>} [client] - An array of client files associated with the trademark.
 * @property {string} [companyCaseNm] - The company case name associated with the trademark.
 * @property {Array<WsFile>} [oppCounsel] - An array of opposing counsel files associated with the trademark.
 * @property {Array<WsFile>} [tmOffice] - An array of trademark office files associated with the trademark.
 */
export interface TrademarkTrifoldData {
  client?: Array<WsFile>;
  companyCaseNm?: string;
  oppCounsel?: Array<WsFile>;
  tmOffice?: Array<WsFile>;
}

/**
 * Trademark
 * @property {string} [companyCaseNm] - The company case name associated with the trademark.
 * @property {number} [trademarkId] - The unique identifier for the trademark.
 */
export interface Trademark {
  companyCaseNm?: string;
  trademarkId?: number;
}

/**
 * Trademark Version Upload Response
 * @property {string} [companyCaseNm] - The company case name associated with the trademark.
 * @property {number} [fileId] - The unique identifier for the uploaded file.
 * @property {string} [uploadUserName] - The username of the user who uploaded the file.
 */
export interface TrademarkUpload {
  companyCaseNm?: string;
  fileId?: number;
  uploadUserName?: string;
}

/**
 * File Check-in Response
 * @property {string} [caseNumber] - The case number associated with the uploaded file.
 * @property {number} [fileId] - The unique identifier for the uploaded file.
 * @property {string} [uploadUserName] - The username of the user who uploaded the file.
 */
export interface UploadCheckin {
  caseNumber?: string;
  fileId?: number;
  uploadUserName?: string;
}

/**
 * Version
 * @property {string} [version] - The version identifier.
 */
export interface Version {
  version?: string;
}

/**
 * Workflow Definition Template
 * @property {number} [id] - The unique identifier of the workflow definition.
 * @property {string} [name] - The name of the workflow definition.
 * @property {DisplayableValueDO} [state] - The state of the workflow definition.
 * @property {WorkflowDefinitionDO.TypeEnum} [type] - The type of the workflow definition.
 */
export interface WorkflowDefinitionDO {
  id?: number;
  name?: string;
  state?: DisplayableValueDO;
  type?: WorkflowDefinitionDO.TypeEnum;
}

/**
 * Workflow Definition Type Enum
 * @enum {string} TypeEnum
 * @property {string} Matter - Represents a workflow definition type for matters.
 * @property {string} Patent - Represents a workflow definition type for patents.
 * @property {string} Trademark - Represents a workflow definition type for trademarks.
 */
export namespace WorkflowDefinitionDO {
  export type TypeEnum = 'Matter' | 'Patent' | 'Trademark';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const TypeEnum = {
    Matter: 'Matter' as TypeEnum,
    Patent: 'Patent' as TypeEnum,
    Trademark: 'Trademark' as TypeEnum,
  };
}

/**
 * Workflow
 * @property {string} [caseNumber] - The case number associated with the workflow.
 * @property {Date} [completeDate] - The date when the workflow was completed.
 * @property {Date} [createDate] - The date when the workflow was created.
 * @property {WorkflowTaskDO} [currentTask] - The current task in the workflow.
 * @property {WorkflowDefinitionDO} [definition] - The definition of the workflow.
 * @property {Date} [dueDate] - The due date for the workflow.
 * @property {number} [id] - The unique identifier of the workflow.
 * @property {string} [instructions] - Instructions associated with the workflow.
 * @property {Date} [modifiedDate] - The date when the workflow was last modified.
 * @property {DisplayableValueDO} [state] - The state of the workflow.
 * @property {boolean} [urgent] - Indicates if the workflow is marked as urgent.
 */
export interface WorkflowDO {
  caseNumber?: string;
  completeDate?: Date;
  createDate?: Date;
  currentTask?: WorkflowTaskDO;
  definition?: WorkflowDefinitionDO;
  dueDate?: Date;
  id?: number;
  instructions?: string;
  modifiedDate?: Date;
  state?: DisplayableValueDO;
  urgent?: boolean;
}

/**
 * Workflow file
 * @property {number} fileId - The unique identifier of the file.
 * @property {IP.TypeEnum} type - The type of the file.
 */
export interface WorkflowFileDO {
  fileId: number;
  type: IP.TypeEnum;
}

/**
 * Workflow Initiation
 * @property {Date} [dueDate] - The due date for the workflow.
 * @property {Array<number>} [files] - An array of file IDs associated with the workflow.
 * @property {string} [instructions] - Instructions for the workflow.
 * @property {number} [urgencyInterval] - The urgency interval for the workflow.
 * @property {WorkflowInitiateDO.UrgencyTypeEnum} [urgencyType] - The type of urgency for the workflow.
 * @property {number} [workflowDefId] - The unique identifier of the workflow definition to be initiated.
 */
export interface WorkflowInitiateDO {
  dueDate?: Date;
  files?: Array<number>;
  instructions?: string;
  urgencyInterval?: number;
  urgencyType?: WorkflowInitiateDO.UrgencyTypeEnum;
  workflowDefId?: number;
}

/**
 * Workflow Initiate Urgency Type Enum
 * @enum {string} UrgencyTypeEnum
 * @property {string} None - Represents no urgency.
 * @property {string} Always - Represents always urgent.
 * @property {string} DaysFromDueDate - Represents urgency based on days from the due date.
 */
export namespace WorkflowInitiateDO {
  export type UrgencyTypeEnum = 'None' | 'Always' | 'DaysFromDueDate';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const UrgencyTypeEnum = {
    None: 'None' as UrgencyTypeEnum,
    Always: 'Always' as UrgencyTypeEnum,
    DaysFromDueDate: 'DaysFromDueDate' as UrgencyTypeEnum,
  };
}

/**
 * Workflow Task Checklist Item Definition
 * @property {string} [name] - The name of the checklist item.
 * @property {number} [order] - The order of the checklist item.
 */
export interface WorkflowTaskChecklistItemDefinitionDO {
  name?: string;
  order?: number;
}

/**
 * Workflow Task Checklist Item
 * @property {string} [name] - The name of the checklist item.
 * @property {number} [order] - The order of the checklist item.
 * @property {WorkflowTaskChecklistItemDO.StatusEnum} [status] - The status of the checklist item.
 */
export interface WorkflowTaskChecklistItemDO {
  name?: string;
  order?: number;
  status?: WorkflowTaskChecklistItemDO.StatusEnum;
}

/**
 * Workflow Task Checklist Item Status Enum
 * @enum {string} StatusEnum
 * @property {string} Complete - Represents a completed checklist item.
 * @property {string} NA - Represents a checklist item that is not applicable.
 * @property {string} Incomplete - Represents an incomplete checklist item.
 */
export namespace WorkflowTaskChecklistItemDO {
  export type StatusEnum = 'Complete' | 'NA' | 'Incomplete';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const StatusEnum = {
    Complete: 'Complete' as StatusEnum,
    NA: 'NA' as StatusEnum,
    Incomplete: 'Incomplete' as StatusEnum,
  };
}

/**
 * Workflow Task Comment
 * @property {string} [comment] - The content of the comment.
 * @property {Date} [createDate] - The date when the comment was created.
 */
export interface WorkflowTaskCommentDO {
  comment?: string;
  createDate?: Date;
}

/**
 * Workflow Task Definition Template
 * @property {Array<WorkflowTaskChecklistItemDefinitionDO>} [checklist] - An array of checklist item definitions for the task.
 * @property {boolean} [checklistRequired] - Indicates if the checklist is required for the task.
 * @property {number} [id] - The unique identifier of the task definition.
 * @property {string} [name] - The name of the task definition.
 * @property {number} [step] - The step number of the task in the workflow.
 * @property {number} [workflowDefId] - The unique identifier of the associated workflow definition.
 * @property {string} [workflowName] - The name of the associated workflow.
 * @property {WorkflowTaskDefinitionDO.WorkflowTypeEnum} [workflowType] - The type of workflow the task belongs to.
 */
export interface WorkflowTaskDefinitionDO {
  checklist?: Array<WorkflowTaskChecklistItemDefinitionDO>;
  checklistRequired?: boolean;
  id?: number;
  name?: string;
  step?: number;
  workflowDefId?: number;
  workflowName?: string;
  workflowType?: WorkflowTaskDefinitionDO.WorkflowTypeEnum;
}

/**
 * Workflow Task Definition Workflow Type Enum
 * @enum {string} WorkflowTypeEnum
 * @property {string} Matter - Represents a workflow type for matters.
 * @property {string} Patent - Represents a workflow type for patents.
 * @property {string} Trademark - Represents a workflow type for trademarks.
 */
export namespace WorkflowTaskDefinitionDO {
  export type WorkflowTypeEnum = 'Matter' | 'Patent' | 'Trademark';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const WorkflowTypeEnum = {
    Matter: 'Matter' as WorkflowTypeEnum,
    Patent: 'Patent' as WorkflowTypeEnum,
    Trademark: 'Trademark' as WorkflowTypeEnum,
  };
}

/**
 * Workflow Task Data Object
 * @property {SimpleUserDO} [assignedTo] - The user to whom the task is assigned.
 * @property {Date} [completeDate] - The date when the task was completed.
 * @property {Date} [createDate] - The date when the task was created.
 * @property {WorkflowTaskDefinitionDO} [definition] - The definition of the task.
 * @property {Date} [dueDate] - The due date for the task.
 * @property {number} [id] - The unique identifier of the task.
 * @property {Date} [modifiedDate] - The date when the task was last modified.
 * @property {WorkflowTaskDO.PriorityEnum} [priority] - The priority level of the task.
 * @property {boolean} [rejectedInPrevStep] - Indicates if the task was rejected in the previous step.
 * @property {boolean} [reviewLoop] - Indicates if the task is in a review loop.
 * @property {DisplayableValueDO} [state] - The state of the task.
 * @property {number} [workflowId] - The unique identifier of the associated workflow.
 */
export interface WorkflowTaskDO {
  assignedTo?: SimpleUserDO;
  completeDate?: Date;
  createDate?: Date;
  definition?: WorkflowTaskDefinitionDO;
  dueDate?: Date;
  id?: number;
  modifiedDate?: Date;
  priority?: WorkflowTaskDO.PriorityEnum;
  rejectedInPrevStep?: boolean;
  reviewLoop?: boolean;
  state?: DisplayableValueDO;
  workflowId?: number;
}
/**
 * Workflow Task Priority Enum
 * @enum {string} PriorityEnum
 * @property {string} Medium - Represents a medium priority level.
 * @property {string} High - Represents a high priority level.
 * @property {string} Low - Represents a low priority level.
 */
export namespace WorkflowTaskDO {
  export type PriorityEnum = 'Medium' | 'High' | 'Low';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  export const PriorityEnum = {
    Medium: 'Medium' as PriorityEnum,
    High: 'High' as PriorityEnum,
    Low: 'Low' as PriorityEnum,
  };
}

/**
 * Workflow User Task Counts
 * @property {number} [groupUnclaimed] - The count of unclaimed tasks in the user's group.
 * @property {number} [userClaimed] - The count of tasks claimed by the user.
 * @property {number} [userUnclaimed] - The count of unclaimed tasks assigned to the user.
 */
export interface WorkflowUserTaskCountsDO {
  groupUnclaimed?: number;
  userClaimed?: number;
  userUnclaimed?: number;
}

/**
 * Account General Settings
 * @property {string} [accountDateFormat] - The date format for the account.
 * @property {string} [apiNotice] - The API notice for the account.
 * @property {string} [apiVersion] - The API version for the account.
 * @property {Object.<string, string>} [customFields] - A dictionary of custom fields for the account.
 * @property {boolean} [displayAssignee] - Indicates if the assignee should be displayed.
 * @property {boolean} [displayMatterTrifoldSubtype] - Indicates if the matter trifold subtype should be displayed.
 * @property {string} [displayMtrDocEventId] - The document event ID for matter display.
 * @property {boolean} [displayNationalFilingDate] - Indicates if the national filing date should be displayed.
 * @property {string} [displayPatCaseLink] - The patent case link display setting.
 * @property {string} [displayPatDocEventId] - The document event ID for patent display.
 * @property {boolean} [displayPatTrifoldSubtype] - Indicates if the patent trifold subtype should be displayed.
 * @property {string} [displayTmDocEventId] - The document event ID for trademark display.
 * @property {boolean} [displayTmTrifoldSubtype] - Indicates if the trademark trifold subtype should be displayed.
 * @property {string} [emailAddress] - The email address associated with the account.
 * @property {string} [exportControlUserDefault] - The default export control setting for the user.
 * @property {string} [firstName] - The first name of the user.
 * @property {Array<AccountGeneralSettings>} [generalSettings] - An array of general settings for the account.
 * @property {string} [lastName] - The last name of the user.
 * @property {string} [matterExportControlDocDefault] - The default export control document setting for matters.
 * @property {string} [matterExportControlStatusDefault] - The default export control status setting for matters.
 * @property {string} [matterTrifoldOrder] - The order of matter trifolds.
 * @property {boolean} [overrideMatterTrifoldSubtype] - Indicates if the matter trifold subtype should be overridden.
 * @property {boolean} [overridePatTrifoldSubtype] - Indicates if the patent trifold subtype should be overridden.
 * @property {boolean} [overrideTmTrifoldSubtype] - Indicates if the trademark trifold subtype should be overridden.
 * @property {string} [patentExportControlDocDefault] - The default export control document setting for patents.
 * @property {string} [patentExportControlStatusDefault] - The default export control status setting for patents.
 * @property {string} [patentTrifoldOrder] - The order of patent trifolds.
 * @property {Object.<string, string>} [preferences] - A dictionary of user preferences.
 * @property {Array<string>} [roles] - An array of roles associated with the user.
 * @property {string} [tmExportControlDocDefault] - The default export control document setting for trademarks.
 * @property {string} [tmExportControlStatusDefault] - The default export control status setting for trademarks.
 * @property {string} [tmTrifoldOrder] - The order of trademark trifolds.
 * @property {string} [userName] - The username of the user.
 * @property {string} [clientName] - The name of the client associated with the account.
 * @property {string} [userSubjectToExportControl] - Indicates if the user is subject to export control regulations.
 */
export interface WsAccountDetails {
  accountDateFormat?: string;
  apiNotice?: string;
  apiVersion?: string;
  customFields?: { [key: string]: string };
  displayAssignee?: boolean;
  displayMatterTrifoldSubtype?: boolean;
  displayMtrDocEventId?: string;
  displayNationalFilingDate?: boolean;
  displayPatCaseLink?: string;
  displayPatDocEventId?: string;
  displayPatTrifoldSubtype?: boolean;
  displayTmDocEventId?: string;
  displayTmTrifoldSubtype?: boolean;
  emailAddress?: string;
  exportControlUserDefault?: string;
  firstName?: string;
  generalSettings?: Array<AccountGeneralSettings>;
  lastName?: string;
  matterExportControlDocDefault?: string;
  matterExportControlStatusDefault?: string;
  matterTrifoldOrder?: string;
  overrideMatterTrifoldSubtype?: boolean;
  overridePatTrifoldSubtype?: boolean;
  overrideTmTrifoldSubtype?: boolean;
  patentExportControlDocDefault?: string;
  patentExportControlStatusDefault?: string;
  patentTrifoldOrder?: string;
  preferences?: { [key: string]: string };
  roles?: Array<string>;
  tmExportControlDocDefault?: string;
  tmExportControlStatusDefault?: string;
  tmTrifoldOrder?: string;
  userName?: string;
  clientName?: string;
  userSubjectToExportControl?: string;
}

/**
 * WebSocket Event
 * @property {number} [createDate] - The date the event was created, represented as a timestamp.
 * @property {string} [eventType] - The type of the event.
 * @property {number} [fileId] - The unique identifier of the file associated with the event.
 * @property {string} [isSubmitted] - Indicates if the event has been submitted.
 * @property {string} [primaryCaseNm] - The primary name of the case associated with the event.
 * @property {string} [referenceName] - The name of the reference associated with the event.
 * @property {Date} [referenceSourceDate] - The source date of the reference associated with the event.
 * @property {number} [referenceSourceId] - The unique identifier of the reference source associated with the event.
 */
export interface WsEvent {
  createDate?: number;
  eventType?: string;
  fileId?: number;
  isSubmitted?: string;
  primaryCaseNm?: string;
  referenceName?: string;
  referenceSourceDate?: Date;
  referenceSourceId?: number;
}

/**
 * File Version Data Object
 * @property {number} [assignDate] - The date the file was assigned, represented as a timestamp.
 * @property {number} [completeDate] - The date the file was completed, represented as a timestamp.
 * @property {string} [docSubtype] - The subtype of the document.
 * @property {number} [docSubtypeId] - The unique identifier of the document subtype.
 * @property {string} [docType] - The type of the document.
 * @property {number} [docTypeId] - The unique identifier of the document type.
 * @property {number} [documentDate] - The date of the document, represented as a timestamp.
 * @property {number} [fileId] - The unique identifier of the file.
 * @property {string} [fileName] - The name of the file.
 * @property {string} [filePriority] - The priority level of the file.
 * @property {number} [fileSize] - The size of the file.
 * @property {string} [fileSource] - The source of the file.
 * @property {string} [fullFileName] - The full name of the file.
 * @property {string} [genericField1] - A generic field for custom use.
 * @property {string} [genericField2] - A generic field for custom use.
 * @property {string} [genericField3] - A generic field for custom use.
 * @property {string} [genericField4] - A generic field for custom use.
 * @property {string} [hasAttachments] - Indicates if the file has attachments.
 * @property {number} [receivedDate] - The date the file was received, represented as a timestamp.
 * @property {number} [receivedDateEpochTime] - The epoch time of the date the file was received.
 * @property {string} [status] - The current status of the file.
 * @property {string} [technicalDocument] - Indicates if the file is a technical document.
 * @property {string} [title] - The title of the file.
 * @property {string} [usersByAssignBy] - The user who assigned the file.
 * @property {string} [usersByAssignTo] - The user to whom the file was assigned.
 * @property {Array<FileVersionDO>} [versions] - An array of versions associated with the file.
 *
 */
export interface WsFile {
  assignDate?: string;
  completeDate?: string;
  currentDueDate?: string;
  docSubtype?: string;
  docSubtypeId?: number;
  docType?: string;
  docTypeId?: number;
  documentDate?: string;
  date?: Date;
  documentDateTime?: string;
  fileId?: number;
  fileName?: string;
  filePriority?: string;
  fileSize?: number;
  fileSource?: string;
  fullFileName?: string;
  genericField1?: string;
  genericField2?: string;
  genericField3?: string;
  genericField4?: string;
  hasAttachments?: string;
  receivedDate?: string;
  receivedDateEpochTime?: number;
  status?: string;
  technicalDocument?: string;
  title?: string;
  usersByAssignBy?: string;
  usersByAssignTo?: string;
  versions?: Array<FileVersionDO>;
}

/**
 * File with Data
 * @property {Blob} [data] - The binary data of the file as a Blob object.
 */
export interface WsFileWithData extends WsFile {
  data?: Blob;
}

/**
 * Keyword
 * @property {string} [keyword] - The keyword text.
 */
export interface WsKeyword {
  keyword?: string;
}

/**
 * Common Note
 * @property {string} [createByUser] - The user who created the note.
 * @property {number} [createDate] - The date the note was created, represented as a timestamp.
 * @property {string} [notes] - The content of the note.
 */
export interface CommonNote {
  createByUser?: string;
  createDate?: number;
  notes?: string;
}

/**
 * File Notes
 * @property {number} [createDate] - The date the note was created, represented as a timestamp.
 * @property {string} [createdByUser] - The user who created the note.
 * @property {number} [fileNoteId] - The unique identifier of the file note.
 * @property {string} [notes] - The content of the file note.
 */
export interface FileNotesDTO {
  createDate?: number;
  createdByUser?: string;
  fileNoteId?: number;
  notes?: string;
}

/**
 * Workflow note
 * @property {number} [createDate] - The date the note was created, represented as a timestamp.
 * @property {string} [note] - The content of the workflow note.
 */
export interface WorkflowNoteDO {
  createDate?: number;
  note?: string;
}
